import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Viewer } from '@react-pdf-viewer/core';
import {
  RenderCurrentPageLabelProps,
  pageNavigationPlugin,
} from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getCreditApplicationPDF,
  getSupplierCreditApplication,
  refreshPlaidReportApplication,
  resolveFeedbackCreditApplication,
  sendReferencesCreditApplication,
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
  editCreditApplicationData,
} from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { getCreditApplicationTemplates } from '../../../../redux/services/creditApplicationsTemplatesService';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import { DillButton, DillDropdownButton } from '../../../general/Dill';
import {
  AppFile,
  ApprovalStatusHistory,
  InternalSignatureDocument,
  InternalSignatureDocumentSignerRole,
  InternalSignatureDocumentSignerSource,
  CreditApplication,
  CreditApplicationFields,
  CreditApplicationFieldsValues,
  CreditApplicationHistoryStatus,
  CreditApplicationStatus,
  InvitedSignatures,
  CreditApplicationType,
  formatMoney,
  getCreditApplicationTypeLabel,
  getFeedbackStatus,
  isBranchSalesRepAssigned,
  isCodApplicationType,
  isExternalFormApplicationType,
  toCreditApplicationType,
} from '@dill/dill-shared';
import {
  DownloadSimple,
  ClockCounterClockwise,
  Flag,
  Lock,
  ShareNetwork,
  PaperPlaneTilt,
  PencilSimple,
  CheckCircle,
} from 'phosphor-react';
import { ChevronDown, ChevronUp, Eye } from 'react-feather';
import {
  firebaseLargeFileUploadHelper,
  openDillToast,
  downloadFile,
  formatBranchLabel,
} from '../../../../utils/helpers';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import successChecksCircle from '../../../../assets/svg/success_checks_circle.svg';
import { AppSuccessModal } from '../../../general/AppSuccessModal/AppSuccessModal';
import {
  getPlaidBankBalance,
  getPlaidBankStatementsPDF,
} from '../../../../redux/services/paymentsService';
import PlaidBalanceModal from '../CreditApplicationComponents/PlaidBalanceModal';
import NotFound from '../CreditApplicationComponents/NotFound';
import CreditReport from '../CreditApplicationComponents/CreditReport';
import CreditApplicationSummaryStep from '../CreditApplicationComponents/CreditApplicationSummaryStep';
import CreditReportModal from '../CreditApplicationComponents/CreditReportModal';
import CreditApplicationFeedbackModal from '../CreditApplicationComponents/CreditApplicationFeedbackModal';
import { COLORS } from '../../../../utils/colors';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import AppCreditApplicationStatus from '../CreditApplications/CreditApplicationStatus';
import NACMCreditReportModal from '../CreditApplicationComponents/NACMCreditReportModal';
import CreditReportCompanyListModal from '../CreditApplicationComponents/CreditReportCompanyListModal';
import ExperianCreditReportModal from '../CreditApplicationComponents/ExperianCreditReportModal';
import ExperianReportCompanyListModal from '../CreditApplicationComponents/ExperianReportCompanyListModal';
import UpdateTradeReferenceDetailsModal from '../CreditApplicationComponents/UpdateTradeReferenceDetailsModal';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import ApproveCreditAppEmailsModal from '../CreditApplicationComponents/ApproveCreditAppEmailModal';
import RejectMessageModal from '../CreditApplicationComponents/RejectMessageModal';
import EditSignatureEmailModal from '../CreditApplicationComponents/EditSignatureEmailModal';
import CreditApplicationNotesModal from '../CreditApplicationComponents/CreditApplicationNotesModal';
import CreditAppHistoryModal from '../CreditApplicationComponents/CreditAppHistoryModal';
import AssignBranchModal from '../CreditApplicationComponents/AssignBranchModal';
import MakeSignatureRequiredModal from '../CreditApplicationComponents/MakeSignatureRequiredModal';
import CreditAppNotes from '../CreditApplicationComponents/CreditAppNotes';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import CreditAppAddTradeReferenceModal from '../CreditApplicationComponents/CreditAppAddTradeReferenceModal';
import CreditAppCommentModal from '../CreditApplicationComponents/CreditAppCommentModal';
import CreditAppHistoryDetailsModal from '../CreditApplicationComponents/CreditAppHistoryDetailsModal';
import ShareApplicationModal from '../CreditApplicationComponents/ShareApplicationModal';
import SendInternalSignatureDocumentsModal from '../CreditApplicationComponents/SendInternalSignatureDocumentsModal';
import {
  InternalSignatureDocumentTemplatePreview,
  InternalSignatureDocumentPlainFieldList,
} from '../CreditApplicationComponents/InternalSignatureDocumentTemplatePreview';
import {
  getInternalSignatureDocuments,
  sendInternalSignatureDocumentReminder,
} from '../../../../redux/services/internalSignatureDocumentService';
import { getUsers } from '../../../../redux/services/authService';
import ReadyForDecisionModal from '../CreditApplicationComponents/ReadyForDecisionModal';
import CreditSafeReportCompanyModal from '../CreditApplicationComponents/CreditSafeReportCompanyModal';
import CreditSafeReportModal from '../CreditApplicationComponents/CreditSafeReportModal';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';

enum ModalState {
  MANUAL_UPLOAD_APPLICATION,
  DILL_APPLICATION,
  NOT_READY,
}

const flattenFieldValues = (
  fields: CreditApplicationFieldsValues[]
): CreditApplicationFieldsValues[] =>
  fields.flatMap((field) => [
    field,
    ...((field.fieldValue ?? []) as unknown as CreditApplicationFieldsValues[]),
  ]);

const CreditApplicationDetails = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { decryptValue } = useDecryption();
  const [currentCreditApplicationId, setCurrentCreditApplicationId] = useState<string>('');
  const [sending, setSending] = useState<string>('');
  const [isUpdateSubSupplierModalOpen, setIsUpdateSubSupplierModalOpen] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [viewBankReferenceStatus, setViewBankReferenceStatus] = useState<string>('');
  const [isPlaidBalanceModalOpen, setIsPlaidBalanceModalOpen] = useState<boolean>(false);
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [feedbackModal, setFeedbackModal] = useState<boolean>(false);
  const [addNotesModal, setAddNotesModal] = useState<boolean>(false);
  const [modalState, setModalState] = useState(ModalState.DILL_APPLICATION);
  const [isCreditReportModalOpen, setIsCreditReportModalOpen] = useState(false);
  const [uploadTradeReferenceResponse, setUploadTradeReferenceResponse] = useState<string>('');
  const [loadingType, setLoadingType] = useState<string>('');
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('');
  const [approvalStatus, setApprovalStatus] = useState<string>('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [tradeRefUpdateDetails, setTradeRefUpdateDetails] = useState<{
    type: 'email' | 'company';
    field: CreditApplicationFieldsValues;
  } | null>(null);
  const [isEditTradeReferenceModalOpen, setIsEditTradeReferenceModalOpen] =
    useState<boolean>(false);
  const [approveEmailModalOpen, setApproveEmailModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [signatureDetails, setSignatureDetails] = useState<InvitedSignatures | null>(null);
  const [signatureEmailModalOpen, setSignatureEmailModalOpen] = useState<boolean>(false);
  const [applicationHistoryModalOpen, setApplicationHistoryModalOpen] = useState<boolean>(false);
  const [makeSignatureModalOpen, setMakeSignatureModalOpen] = useState<boolean>(false);
  const [selectedSignature, setSelectedSignature] = useState<InvitedSignatures | null>(null);
  const [selectedTradeRefFields, setSelectedTradeRefFields] = useState<
    CreditApplicationFieldsValues[]
  >([]);
  const [addTradeRefModalOpen, setAddTradeeRefModalOpen] = useState<boolean>(false);
  const [creditAppCommentModalOpen, setCreditAppCommentModalOpen] = useState<boolean>(false);
  const [creditAppCommentModalType, setCreditAppCommentModalType] =
    useState<string>('resend-signature');
  const [isFeedbackDropdownOpen, setIsFeedbackDropdownOpen] = useState(false);
  // Date (ISO) of the feedback entry being resolved, which is how the backend identifies it.
  const [resolvingFeedbackDate, setResolvingFeedbackDate] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'personal' | 'business' | null>(null);
  const [activeStep, setActiveStep] = useState<string>('');
  const [allSteps, setAllSteps] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isEditingApplicationData, setIsEditingApplicationData] = useState<boolean>(false);
  const [editableFieldValues, setEditableFieldValues] = useState<CreditApplicationFieldsValues[]>(
    []
  );
  const [savingApplicationData, setSavingApplicationData] = useState<boolean>(false);
  const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});

  const zoomPluginInstance = zoomPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const { CurrentPageLabel } = pageNavigationPluginInstance;

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const { creditApplication, loadingList } = useAppSelector((state) => state.creditApplications);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );
  const [decryptedCreditApplication, setDecryptedCreditApplication] = useState<
    (CreditApplication & { applicationHistoryId: string | null }) | null
  >(null);
  const [learnMoreModalOpen, setLearnMoreModalOpen] = useState<boolean>(false);
  const [creditReportCompanyList, setCreditReportCompanyList] = useState<
    {
      id: string;
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      telephone: string;
      confidence: string | number | null;
      tradelines: string | number | null;
    }[]
  >([]);
  const [learnMoreData, setLearnMoreData] = useState<string>('');
  const [creditReportError, setCreditReportError] = useState<string>('');
  const [nacmId, setNacmId] = useState<string>('');
  const [creditSafeId, setCreditSafeId] = useState<string>('');
  const [creditSafeData, setCreditSafeData] = useState<any>(null);
  const [creditReportCompanyListModalOpen, setCreditReportCompanyListModalOpen] =
    useState<boolean>(false);
  const [experianReportModalOpen, setExperianReportModalOpen] = useState<boolean>(false);
  const [experianCompanyListModalOpen, setExperianCompanyListModalOpen] = useState<boolean>(false);
  const [experianBin, setExperianBin] = useState<string>('');
  const [experianReportData, setExperianReportData] = useState<any>(null);
  const [experianReportBufferData, setExperianReportBufferData] = useState<{
    data: number[];
  } | null>(null);
  const [assignBranchModalOpen, setAssignBranchModalOpen] = useState(false);
  const [approvalStatusHistoryDetailsModalOpen, setApprovalStatusHistoryDetailsModalOpen] =
    useState<boolean>(false);
  const [approvalStatusHistoryDetails, setApprovalStatusHistoryDetails] =
    useState<ApprovalStatusHistory | null>(null);
  const [mode, setMode] = useState<
    'NOTIFY' | 'INTERNALLY_APPROVE' | 'INTERNALLY_APPROVE_COD' | 'INTERNALLY_REJECT' | null
  >(null);
  const [shareApplicationModalOpen, setShareApplicationModalOpen] = useState<boolean>(false);
  const [readyForDecisionModalOpen, setReadyForDecisionModalOpen] = useState<boolean>(false);
  const [creditSafeReportModalOpen, setCreditSafeReportModalOpen] = useState<boolean>(false);
  const [creditSafeReportDetailsModalOpen, setCreditSafeReportDetailsModalOpen] =
    useState<boolean>(false);
  const [creditSafePage, setCreditSafePage] = useState<number>(1);
  const [internalSignatureDocuments, setInternalSignatureDocuments] = useState<
    InternalSignatureDocument[]
  >([]);
  const [internalSignatureDocumentFieldsById, setInternalSignatureDocumentFieldsById] = useState<
    Record<string, CreditApplicationFields[]>
  >({});
  const [sendInternalSignatureDocumentsModalOpen, setSendInternalSignatureDocumentsModalOpen] =
    useState<boolean>(false);
  const [sendingReminderForRole, setSendingReminderForRole] =
    useState<InternalSignatureDocumentSignerRole | null>(null);

  // A history version (?historyId=) has its own internal signature documents, keyed by its id.
  const internalSignatureDocumentsOwnerId =
    searchParams.get('historyId') || currentCreditApplicationId;

  const fetchInternalSignatureDocuments = async (creditApplicationId: string) => {
    if (!currentSupplier?.id || !creditApplicationId) return;
    const result = await dispatch(
      getInternalSignatureDocuments({ supplierId: currentSupplier.id, creditApplicationId })
    );
    if (result.type.includes('fulfilled')) {
      const payload = (result as any).payload;
      setInternalSignatureDocuments(payload?.data?.internalSignatureDocuments ?? []);
      setInternalSignatureDocumentFieldsById(payload?.data?.fillableFieldsByFormId ?? {});
    }
  };

  // Finds a field's value by id anywhere on an internal signature document — its own top-level
  // fieldValues, OR nested one level down inside a CONDITIONAL_SELECTED_FIELD's own `fieldValue`
  // sub-answers. "charge-existing-account-number" specifically only ever exists nested under
  // "charge-account-type" (it's the sub-field revealed once "Convert Cash to Charge" is picked),
  // never as a top-level entry — a plain top-level .find() never finds it.
  const findInternalSignatureDocumentFieldValue = (
    fieldValues: CreditApplicationFieldsValues[],
    id: string
  ): unknown => {
    for (const fv of fieldValues) {
      if (fv.id === id) return fv.value;
      const nested = (fv.fieldValue ?? []).find((sub) => sub.id === id);
      if (nested) return nested.value;
    }
    return undefined;
  };

  // Branch Coversheet's "Account Type" (charge-account-type) / "Existing Account Number"
  // (charge-existing-account-number) fields — when the Branch Manager picked "Convert Cash to
  // Charge" (this customer already has a P21 account), the Approve Credit modal should default to
  // Update Customer with that account number pre-filled, instead of starting blank. Checked across
  // every internal signature document on this application (whichever one carries these ids), same
  // "first non-blank answer wins" lookup used for other coversheet-sourced values.
  const chargeAccountType = useMemo(() => {
    for (const doc of internalSignatureDocuments) {
      const value = findInternalSignatureDocumentFieldValue(doc.fieldValues, 'charge-account-type');
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }, [internalSignatureDocuments]);
  const chargeExistingAccountNumber = useMemo(() => {
    for (const doc of internalSignatureDocuments) {
      const value = findInternalSignatureDocumentFieldValue(
        doc.fieldValues,
        'charge-existing-account-number'
      );
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }, [internalSignatureDocuments]);

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const creditApplicationId = pathlist[pathlist.length - 1];
    setCurrentCreditApplicationId(creditApplicationId);
    if (currentSupplier?.id && user?.roles?.includes('SUPPLIER')) {
      fetchInternalSignatureDocuments(searchParams.get('historyId') || creditApplicationId);
      dispatch(
        getSupplierCreditApplication({
          supplierId: currentSupplier?.id || '',
          creditApplicationId: creditApplicationId,
          hashedType: 'encrypted',
          fetchType: 'detailed',
        })
      );
    }
  }, [location, currentSupplier?.id]);

  useEffect(() => {
    if (currentSupplier?.id && user?.roles?.includes('SUPPLIER')) {
      dispatch(
        getUsers({
          data: {
            roles: ['CREDIT_APP_DECISION_MAKER'],
            // companyId: currentSupplier.id,
            linkedSupplierIds: [currentSupplier.id],
          },
        })
      );
    }
  }, [currentSupplier?.id]);

  useEffect(() => {
    if (currentSupplier?.id && creditApplicationTemplates.length === 0) {
      dispatch(getCreditApplicationTemplates({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, creditApplicationTemplates.length, dispatch]);

  useEffect(() => {
    let isMounted = true;

    const decrypt = async () => {
      if (!creditApplication) {
        if (isMounted) setDecryptedCreditApplication(null);
        return;
      }

      if (creditApplication.templateId === '') {
        const isNotReady =
          creditApplication.status === 'SENT' || creditApplication.status === 'OPENED';
        if (isMounted)
          setModalState(isNotReady ? ModalState.NOT_READY : ModalState.MANUAL_UPLOAD_APPLICATION);
      } else if (creditApplication.templateId) {
        const isNotReady =
          creditApplication.status === 'SENT' || creditApplication.status === 'OPENED';
        setModalState(isNotReady ? ModalState.NOT_READY : ModalState.DILL_APPLICATION);
      } else {
        if (isMounted) setModalState(ModalState.DILL_APPLICATION);
      }
      const historyId = searchParams.get('historyId');
      const resolvedHistory = historyId
        ? (creditApplication.applicationUpdateHistory ?? []).find((h) => h.id === historyId) ?? null
        : null;

      const creditApplicationCopy = resolvedHistory
        ? {
            ...resolvedHistory,
            id: creditApplication.id,
            applicationHistoryId: resolvedHistory.id,
          }
        : { ...creditApplication, applicationHistoryId: null };

      const { decryptedFields, decryptedBankReferences, decryptedInvitedSignatures } =
        await decryptCreditApplicationData(creditApplicationCopy, decryptValue, true, true);

      if (isMounted) {
        setDecryptedCreditApplication({
          ...creditApplicationCopy,
          fieldValues: decryptedFields,
          bankReference: decryptedBankReferences,
          invitedSignatures: decryptedInvitedSignatures,
        });
      }
    };

    decrypt();

    return () => {
      isMounted = false;
    };
  }, [creditApplication, searchParams]);

  const sortStepNames = (fields: CreditApplicationFieldsValues[]) => {
    const uniqueStepsMap = new Map<string, number>();

    for (const field of fields) {
      if (!uniqueStepsMap.has(field.stepName)) {
        uniqueStepsMap.set(field.stepName, field.stepNumber);
      }
    }

    const sortedStepNames = [...uniqueStepsMap.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([stepName]) => stepName);

    return sortedStepNames;
  };

  const stepName = useMemo(() => {
    if (decryptedCreditApplication && decryptedCreditApplication.fieldValues) {
      return sortStepNames(decryptedCreditApplication.fieldValues);
    }
    return [];
  }, [decryptedCreditApplication]);

  const applicationTemplate = useMemo(
    () =>
      decryptedCreditApplication?.templateId
        ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
        : undefined,
    [creditApplicationTemplates, decryptedCreditApplication?.templateId]
  );

  // "Credit Application" / "COD Application", or an external form's template name.
  const applicationTypeTitle = getCreditApplicationTypeLabel(
    decryptedCreditApplication?.applicationType,
    { long: true, formName: applicationTemplate?.name }
  );

  // The same, for running copy: "credit application" / "COD application" / the form's name.
  const getApplicationTypeName = (applicationType?: string | null) =>
    isExternalFormApplicationType(applicationType)
      ? getCreditApplicationTypeLabel(applicationType, { formName: applicationTemplate?.name })
      : isCodApplicationType(applicationType)
      ? 'COD application'
      : 'credit application';

  const conditionalVisibilityById = useMemo(() => {
    const template = decryptedCreditApplication?.templateId
      ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
      : null;
    const map = new Map<string, { sourceFieldId: string; sourceOptionValues: string[] }>();
    template?.steps?.forEach((step) => {
      step.fields?.forEach((field) => {
        field.unGroupedFields?.forEach((f) => {
          if (f.conditionalVisibility) map.set(f.id, f.conditionalVisibility);
        });
        field.groupedFields?.forEach((group) => {
          group.fields?.forEach((f) => {
            if (f.conditionalVisibility) map.set(f.id, f.conditionalVisibility);
          });
        });
      });
    });

    (decryptedCreditApplication?.specificRules ?? []).forEach((rule) => {
      if (rule.hidden === undefined) return;
      if (rule.hidden) {
        map.set(rule.fieldId, {
          sourceFieldId: '__specific_rule_hidden__',
          sourceOptionValues: [],
        });
      } else {
        map.delete(rule.fieldId);
      }
    });
    return map;
  }, [
    decryptedCreditApplication?.templateId,
    decryptedCreditApplication?.specificRules,
    creditApplicationTemplates,
  ]);

  const templateFieldById = useMemo(() => {
    const template = decryptedCreditApplication?.templateId
      ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
      : null;
    const map = new Map<
      string,
      { validationType?: CreditApplicationFieldsValues['validationType']; isFieldEmail?: boolean }
    >();
    template?.steps?.forEach((step) => {
      step.fields?.forEach((stepField) => {
        stepField.unGroupedFields?.forEach((f) => {
          map.set(f.id, { validationType: f.validationType, isFieldEmail: f.isFieldEmail });
        });
        stepField.groupedFields?.forEach((group) => {
          group.fields?.forEach((f) => {
            map.set(f.id, { validationType: f.validationType, isFieldEmail: f.isFieldEmail });
          });
        });
      });
    });
    return map;
  }, [decryptedCreditApplication?.templateId, creditApplicationTemplates]);

  const documentTextById = useMemo(() => {
    const template = decryptedCreditApplication?.templateId
      ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
      : null;
    const map = new Map<string, string>();
    template?.steps?.forEach((step) => {
      step.fields?.forEach((field) => {
        field.unGroupedFields?.forEach((f) => {
          if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
        });
        field.groupedFields?.forEach((group) => {
          group.fields?.forEach((f) => {
            if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
          });
        });
      });
    });
    return map;
  }, [decryptedCreditApplication?.templateId, creditApplicationTemplates]);

  const previewFormData = useMemo(() => {
    const fieldValues = decryptedCreditApplication?.fieldValues ?? [];

    if (decryptedCreditApplication?.status === 'DRAFT') {
      const template = decryptedCreditApplication?.templateId
        ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
        : null;
      if (template) {
        const fieldValueById = new Map(fieldValues.map((fv) => [fv.id, fv]));
        const rows: CreditApplicationFieldsValues[] = [];

        template.steps?.forEach((step, stepIndex) => {
          step.fields?.forEach((stepField) => {
            if (stepField.stepType === 'GROUPED') {
              const groupedFieldIds = new Set(
                (stepField.groupedFields ?? []).flatMap((group) =>
                  (group.fields ?? []).map((f) => f.id)
                )
              );
              fieldValues.forEach((fv) => {
                // Repeated groups save their fields as `<template id>-group-N`; match on the base
                // id or every group past the first is hidden from the preview.
                const baseId = fv.id.replace(/-group-\d+$/, '');
                if (!groupedFieldIds.has(baseId)) return;
                const conditionalVisibility = conditionalVisibilityById.get(baseId);
                rows.push({
                  ...fv,
                  ...(conditionalVisibility ? { conditionalVisibility } : {}),
                });
              });
              return;
            }
            if (stepField.stepType !== 'NOT_GROUPED') return;
            stepField.unGroupedFields?.forEach((f) => {
              const conditionalVisibility = conditionalVisibilityById.get(f.id);
              const existing = fieldValueById.get(f.id);
              if (existing) {
                rows.push({
                  ...existing,
                  ...(conditionalVisibility ? { conditionalVisibility } : {}),
                });
                return;
              }

              let value: CreditApplicationFieldsValues['value'] = null;
              if (f.fromField === 'DOCUMENT_FIELD') {
                value = f.documentText || '';
              } else if (
                f.fromField === 'TEXT' ||
                f.fromField === 'TEXT_AREA' ||
                f.fromField === 'OPTION_FIELD' ||
                f.fromField === 'NUMBER' ||
                f.fromField === 'CURRENCY'
              ) {
                value = '';
              } else if (f.fromField === 'CHECK_BOX' || f.fromField === 'AUTOFILL_CHECKBOX') {
                value = false;
              }

              rows.push({
                uniqueId: `template-${f.id}`,
                id: f.id,
                name: f.name,
                fromField: f.fromField,
                isRequired: f.isRequired || false,
                isHashed: f.isHashed || false,
                stepName: step.name,
                stepNumber: step.position ?? stepIndex,
                groupStepPosition: stepField.position || 0,
                groupedPosition: 0,
                groupName: stepField.groupName || '',
                groupColumnType: stepField.columnType || 'ONE_COLUMN',
                isFieldArray: stepField.isFieldArray || false,
                isFieldEmail: f.isFieldEmail || false,
                index: 0,
                value,
                ...(f.optionType ? { optionType: f.optionType } : {}),
                ...(f.type ? { type: f.type } : {}),
                ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
                ...(f.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
                ...(f.populateFieldSignatureId
                  ? { populateFieldSignatureId: f.populateFieldSignatureId }
                  : {}),
                ...(f.minimumCreditAmount !== undefined
                  ? { minimumCreditAmount: f.minimumCreditAmount }
                  : {}),
                ...(f.minimumNumberOfSignatures !== undefined
                  ? { minimumNumberOfSignatures: f.minimumNumberOfSignatures }
                  : {}),
                ...(f.signaturePageFieldIds
                  ? { signaturePageFieldIds: f.signaturePageFieldIds }
                  : {}),
                ...(conditionalVisibility ? { conditionalVisibility } : {}),
              } as CreditApplicationFieldsValues);
            });
          });
        });
        return rows;
      }
    }

    if (conditionalVisibilityById.size === 0 && documentTextById.size === 0) return fieldValues;
    return fieldValues.map((fv) => {
      const conditionalVisibility = conditionalVisibilityById.get(fv.id);
      const needsDocumentTextFallback =
        fv.fromField === 'DOCUMENT_FIELD' && !fv.value && documentTextById.has(fv.id);
      if (!conditionalVisibility && !needsDocumentTextFallback) return fv;
      return {
        ...fv,
        ...(conditionalVisibility ? { conditionalVisibility } : {}),
        ...(needsDocumentTextFallback ? { value: documentTextById.get(fv.id) } : {}),
      };
    });
  }, [
    decryptedCreditApplication?.fieldValues,
    decryptedCreditApplication?.status,
    decryptedCreditApplication?.templateId,
    conditionalVisibilityById,
    documentTextById,
    creditApplicationTemplates,
  ]);

  const pulledCreditReports = useMemo(
    () => [
      ...(decryptedCreditApplication?.nacmReports || []).map((nacm) => ({
        ...nacm,
        source: 'NACM',
      })),
      ...(decryptedCreditApplication?.creditSafeReports || []).map((creditSafe) => ({
        ...creditSafe,
        source: 'CreditSafe',
      })),
      ...(decryptedCreditApplication?.manualCreditReports || []).map((manual) => ({
        ...manual,
        source: 'Manual',
      })),
      ...(decryptedCreditApplication?.experianReports || []).map((experian) => ({
        ...experian,
        source: 'Experian',
      })),
    ],
    [
      decryptedCreditApplication?.nacmReports,
      decryptedCreditApplication?.creditSafeReports,
      decryptedCreditApplication?.manualCreditReports,
      decryptedCreditApplication?.experianReports,
    ]
  );

  // Build step list from rendered h2 elements — captures bank ref, signature, credit score, etc.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const timer = setTimeout(() => {
      const headers = Array.from(container.querySelectorAll<HTMLHeadingElement>('h2'));
      const names = [...new Set(headers.map((h) => h.textContent?.trim() ?? '').filter(Boolean))];
      setAllSteps(names);
      if (names.length > 0) setActiveStep(names[0]);
    }, 300);
    return () => clearTimeout(timer);
  }, [decryptedCreditApplication?.id]);

  // Smooth scroll tracking with requestAnimationFrame
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || allSteps.length === 0) return;
    let rafId: number;
    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headers = Array.from(container.querySelectorAll<HTMLHeadingElement>('h2'));
        const containerRect = container.getBoundingClientRect();
        let current = '';
        for (const h of headers) {
          const top = h.getBoundingClientRect().top - containerRect.top;
          if (top <= 80) current = h.textContent?.trim() ?? '';
        }
        if (current) setActiveStep(current);
      });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [allSteps]);

  const handleStepClick = (step: string) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const headers = Array.from(container.querySelectorAll<HTMLHeadingElement>('h2'));
    const target = headers.find((h) => h.textContent?.trim() === step);
    if (!target) return;
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollBy({ top: offset - 16, behavior: 'smooth' });
    setActiveStep(step);
  };

  // Scrolls the preview to the first field found for a clicked fraud check and briefly highlights
  // it. Exact ids are tried first, then substring matches (e.g. 'ein-number' inside a grouped or
  // nested id), skipping trade reference fields.
  const handleSelectFraudCheckFields = (fieldIds: string[]) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const fields = Array.from(container.querySelectorAll<HTMLElement>('[data-field-id]'));
    const idOf = (el: HTMLElement) => el.dataset.fieldId ?? '';
    const target =
      fieldIds
        .map((id) => fields.find((el) => idOf(el).replace(/-group-\d+$/, '') === id))
        .find(Boolean) ??
      fieldIds
        .map((id) =>
          fields.find((el) => idOf(el).includes(id) && !idOf(el).includes('trade-reference'))
        )
        .find(Boolean);
    if (!target) {
      openDillToast({ type: 'ERROR', message: 'That field isn’t on this application.' });
      return;
    }
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollBy({ top: offset - 24, behavior: 'smooth' });
    target.classList.add('ring-2', 'ring-amber-300', 'rounded-md', 'transition-shadow');
    window.setTimeout(() => {
      target.classList.remove('ring-2', 'ring-amber-300', 'rounded-md', 'transition-shadow');
    }, 2000);
  };

  const handleEmailAction = async (
    fields: CreditApplicationFieldsValues[],
    indx: number,
    type: 'initial' | 'followUp'
  ) => {
    setSending(`${type}_${indx}`);

    const emailField = fields.find((field) => field.id.includes('email'));
    if (!emailField) {
      openDillToast({
        type: 'ERROR',
        message: 'Email not found in the data.',
      });
      return;
    }

    let key = 'requested';
    if (type === 'followUp') {
      key = 'followUp';
    }

    const updatedFields = fields.map((field) => ({
      ...field,
      sendEmail: true,
      status: field.status === 'Submitted' ? field.status : ('Requested' as const),
      referenceHistoryDate: [
        ...(field.referenceHistoryDate ?? []),
        {
          [key]: moment().format('MM-DD-YYYY LT'),
          actionBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
          action: key,
          note: `Requested ${key === 'requested' ? 'trade reference' : 'a follow up'} from ${
            emailField?.value ?? ''
          }.`,
          date: moment().format('MM-DD-YYYY LT'),
        },
      ],
    }));

    const results = await dispatch(
      sendReferencesCreditApplication({
        fieldValues: updatedFields,
        supplierId: currentSupplier?.id || '',
        creditApplicationId: currentCreditApplicationId,
        sendReferenceEmail: type,
        applicationHistoryId: decryptedCreditApplication?.applicationHistoryId,
      })
    );

    if (results.type === 'creditApplications/sendReferencesCreditApplication/fulfilled') {
      openDillToast({
        type: 'SUCCESS',
        message:
          type === 'initial' ? 'Email sent successfully.' : 'Follow up email sent successfully.',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: type === 'initial' ? 'Failed to send email.' : 'Failed to send follow up email.',
      });
    }

    setSending('');
  };

  const handleAddTradeReference = async (fields: CreditApplicationFieldsValues[]) => {
    setSelectedTradeRefFields(fields);
    setAddTradeeRefModalOpen(true);
  };

  const handleUpdateCreditApplication = async (
    status:
      | 'FULLY_APPROVED'
      | 'REJECTED_BUSINESS'
      | 'PARTIALLY_APPROVED'
      | 'REJECTED_WITH_COD'
      | 'SUBMITTED',
    applicationType?: CreditApplicationType,
    rejectVal?: string
  ) => {
    if (!currentCreditApplicationId) {
      openDillToast({
        type: 'ERROR',
        message: 'Credit Application ID is not available.',
      });
      return;
    }
    const currentAppStatus = status.includes('APPROVED')
      ? 'APPROVED'
      : status.includes('REJECTED')
      ? 'REJECTED'
      : 'SUBMITTED';
    setLoadingStatus(status);
    setCurrentStatus(currentAppStatus);
    let additionalData = {};
    if (applicationType) {
      additionalData = { ...additionalData, applicationType };
    }
    if (rejectVal) {
      additionalData = { ...additionalData, rejectReason: rejectVal };
    }
    const approvalStatusHistory = [...(decryptedCreditApplication?.approvalStatusHistory ?? [])]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((item) => item.status);

    if (
      (approvalStatusHistory.includes('APPROVED_COD') ||
        approvalStatusHistory.includes('INTERNALLY_APPROVED_COD')) &&
      status === 'SUBMITTED'
    ) {
      // Back on credit terms - an external form goes back to being one rather than a plain credit app.
      additionalData = {
        ...additionalData,
        applicationType:
          toCreditApplicationType(applicationTemplate?.applicationType) ?? 'CREDIT_APPLICATION',
      };
    }

    if (status === 'SUBMITTED') {
      additionalData = { ...additionalData, status: 'READY_FOR_DECISION' };
    }

    const amount = decryptedCreditApplication?.fieldValues?.find((field) =>
      field.id.includes('amount-of')
    )?.value;
    if (amount && status.includes('APPROVED') && typeof amount === 'number') {
      additionalData = { ...additionalData, amountAproved: amount };
    }

    setLoadingStatus('');
    let results = null;
    if (!decryptedCreditApplication?.applicationHistoryId) {
      results = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: currentCreditApplicationId,
          status: currentAppStatus,
          supplierId: currentSupplier?.id || '',
          emailsToNotify: [decryptedCreditApplication?.senderEmail || ''],
          ...additionalData,
        })
      );
    } else {
      results = await dispatch(
        updateApplicationUpdateHistory({
          applicationHistoryId: decryptedCreditApplication?.applicationHistoryId ?? '',
          creditApplicationId: currentCreditApplicationId,
          status: currentAppStatus,
          supplierId: currentSupplier?.id || '',
          ...additionalData,
        })
      );
    }
    if (results && results.type.includes('fulfilled')) {
      setIsUpdateSubSupplierModalOpen(true);
      setLoadingStatus('');
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to update credit application.',
      });
      setLoadingStatus('');
    }
  };

  const handleRejectApplication = (
    status: 'REJECTED_BUSINESS' | 'REJECTED_WITH_COD',
    applicationType?: CreditApplicationType,
    rejectVal?: string
  ) => {
    dispatch(
      openMessageModal({
        title: 'Confirm Rejection',
        subTitle: `Are you sure you want to reject this ${getApplicationTypeName(
          applicationType
        )}?`,
        buttonText: 'No, Cancel',
        isSecondButton: true,
        secondButtonText: 'Yes, Reject',
        modalType: 'WARN',
        onSecondButtonClick: async () => {
          handleUpdateCreditApplication(status, applicationType, rejectVal);
          setIsRejectModalOpen(false);
        },
      })
    );
  };

  const handleGetBankBalance = async (accessToken: string) => {
    setViewBankReferenceStatus('balance');
    const results = await dispatch(
      getPlaidBankBalance({
        accessToken,
      })
    );
    if (results.type === 'payments/getPlaidBankBalance/fulfilled') {
      const payload = results?.payload as any;
      setBankDetails(payload?.data?.data?.accountBalances ?? null);
      setViewBankReferenceStatus('');
      setIsPlaidBalanceModalOpen(true);
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to retrieve bank balance.',
      });
      setViewBankReferenceStatus('');
    }
  };

  const handleGetBankTransactions = async (accessToken: string, month: string) => {
    setViewBankReferenceStatus(`${month}_statements`);
    const results = await dispatch(
      getPlaidBankStatementsPDF({
        accessToken,
        date: month,
        monthsRange: 12,
      })
    );
    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: 'Bank statement successfully downloaded.',
      });
      setViewBankReferenceStatus('');
    } else {
      let errorMessage = 'Failed to download bank statement.';
      if (results.payload && typeof results.payload === 'string') {
        errorMessage = results.payload;
      }
      openDillToast({
        type: 'ERROR',
        message: errorMessage,
      });
      setViewBankReferenceStatus('');
    }
  };

  const handleGetPlaidDetails = (
    accessToken: string,
    type: 'balance' | 'statements',
    month: string
  ) => {
    if (type === 'balance') {
      handleGetBankBalance(accessToken);
    } else {
      handleGetBankTransactions(accessToken, month);
    }
  };

  const handleRefreshPlaidDetails = async () => {
    const refreshPlaidResults = await dispatch(
      refreshPlaidReportApplication({
        supplierId: currentSupplier?.id ?? '',
        creditApplicationId: currentCreditApplicationId,
      })
    );
    if (refreshPlaidResults.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: 'Plaid data refreshed successfully.',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to refresh plaid data.',
      });
    }
  };

  const handleUploadTradeReferenceResponse = async ({
    files,
    text,
    fields,
  }: {
    files: { value: File; id: string }[];
    text: { value: string; id: string } | null;
    fields: CreditApplicationFieldsValues[];
  }) => {
    const id = text?.id || files[0]?.id || '';
    setUploadTradeReferenceResponse(`loading_${id}`);

    const existingTradeResponses = decryptedCreditApplication?.tradeReferenceResponse ?? {};

    const tradeRefData: Record<string, { textResponse?: string | null; files?: AppFile[] | null }> =
      {
        ...existingTradeResponses,
      };

    const uploadedFiles: AppFile[] = [...(tradeRefData[id]?.files ?? [])];

    if (files.length > 0) {
      for (const file of files) {
        const fileType = file.value.name.split('.').pop() || '';
        const uploadFileUrl = await firebaseLargeFileUploadHelper({
          blobUrl: URL.createObjectURL(file.value),
          fileNameWithExtension: file.value.name,
          storagePath: `creditApplicationFiles/${
            decryptedCreditApplication?.supplierId || ''
          }/${currentCreditApplicationId}/${Date.now()}`,
        });
        if (uploadFileUrl) {
          uploadedFiles.push({
            name: file.value.name,
            url: uploadFileUrl,
            fileType,
          });
        }
      }
    }

    tradeRefData[id] = {
      ...tradeRefData[id],
      files: uploadedFiles,
      textResponse: text?.value?.trim() || tradeRefData[id]?.textResponse || null,
    };

    const payload = {
      tradeReferenceResponse: tradeRefData,
      fieldValues: fields,
    };

    let response = null;
    if (!decryptedCreditApplication?.applicationHistoryId) {
      response = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: currentCreditApplicationId,
          supplierId: currentSupplier?.id || '',
          ...payload,
        })
      );
    } else {
      response = await dispatch(
        updateApplicationUpdateHistory({
          applicationHistoryId: decryptedCreditApplication?.applicationHistoryId ?? '',
          creditApplicationId: currentCreditApplicationId,
          supplierId: currentSupplier?.id || '',
          ...payload,
        })
      );
    }

    if (response && response.type.includes('fulfilled')) {
      openDillToast({
        message: `Trade reference file successfully uploaded!`,
        type: 'SUCCESS',
      });
    } else {
      openDillToast({
        message: `Trade reference file failed to upload!`,
        type: 'ERROR',
      });
    }

    setUploadTradeReferenceResponse('');
  };

  const companyDetails = useMemo(() => {
    const details: {
      companyName: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      telephone?: string;
    } = { companyName: '' };

    if (decryptedCreditApplication) {
      const getFieldValue = (idFragment: string) => {
        const field = decryptedCreditApplication.fieldValues?.filter(
          (f) => f.id.includes(idFragment) && f.stepNumber === 1
        );
        if (field?.length > 0 && typeof field[0]?.value === 'string') {
          return field[0].value.trim();
        }
        return undefined;
      };

      details.companyName = getFieldValue('customer-or-company-name') ?? '';
      details.address = getFieldValue('address');
      details.city = getFieldValue('city');
      details.state = getFieldValue('state');
      details.zipCode = getFieldValue('zip');
      details.telephone = getFieldValue('phone');
    }

    return details;
  }, [decryptedCreditApplication]);

  const handleDeleteTradeReferenceFile = async (id: string, url: string) => {
    if (
      decryptedCreditApplication?.tradeReferenceResponse &&
      decryptedCreditApplication?.tradeReferenceResponse?.[id] &&
      decryptedCreditApplication?.tradeReferenceResponse?.[id]?.files
    ) {
      const tradeReference = decryptedCreditApplication?.tradeReferenceResponse?.[id];
      const files = decryptedCreditApplication?.tradeReferenceResponse?.[id]?.files;
      const filteredFiles = files?.filter((file) => file.url !== url);
      const details = {
        ...tradeReference,
        files: filteredFiles,
      };
      const tradeRefData = {
        [id]: details,
      };

      let response = null;
      if (!decryptedCreditApplication?.applicationHistoryId) {
        response = await dispatch(
          updateSupplierCreditApplication({
            creditApplicationId: currentCreditApplicationId,
            supplierId: currentSupplier?.id || '',
            tradeReferenceResponse: tradeRefData,
          })
        );
      } else {
        response = await dispatch(
          updateApplicationUpdateHistory({
            applicationHistoryId: decryptedCreditApplication?.applicationHistoryId ?? '',
            creditApplicationId: currentCreditApplicationId,
            supplierId: currentSupplier?.id || '',
            tradeReferenceResponse: tradeRefData,
          })
        );
      }

      if (response && response.type.includes('fulfilled')) {
        openDillToast({
          message: `File successfully removed!`,
          type: 'SUCCESS',
        });
      } else {
        openDillToast({
          message: `Failed to remove file!`,
          type: 'ERROR',
        });
      }
    }
  };

  const handlePreviewOrDownload = async (type: string) => {
    setLoadingType(type);
    const results = await dispatch(
      getCreditApplicationPDF({
        creditApplicationId: currentCreditApplicationId,
        preview: type === 'preview',
        supplierId: currentSupplier?.id || '',
      })
    );
    if (results.type === 'creditApplications/getCreditApplicationPDF/fulfilled') {
      const payload = results.payload as { data: { pdfUrl: string } };
      if (type === 'preview') {
        if (!payload?.data?.pdfUrl) {
          openDillToast({
            type: 'ERROR',
            message: 'Failed to fetch PDF',
          });
          setLoadingType('');
          return;
        } else {
          setCurrentPdfUrl(payload?.data?.pdfUrl);
          setPreviewModalOpen(true);
        }
      }
      setLoadingType('');
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to fetch PDF',
      });
      setLoadingType('');
    }
  };

  const handleStartEditingApplicationData = () => {
    const fieldValuesSnapshot: CreditApplicationFieldsValues[] = JSON.parse(
      JSON.stringify(decryptedCreditApplication?.fieldValues ?? [])
    );
    setEditableFieldValues(
      fieldValuesSnapshot.map((field) => {
        const templateField = templateFieldById.get(field.id);
        if (!templateField) return field;
        return {
          ...field,
          validationType: field.validationType ?? templateField.validationType,
          isFieldEmail: field.isFieldEmail || templateField.isFieldEmail || false,
        };
      })
    );
    setEditFieldErrors({});
    setIsEditingApplicationData(true);
  };

  const handleCancelEditingApplicationData = () => {
    setIsEditingApplicationData(false);
    setEditableFieldValues([]);
    setEditFieldErrors({});
  };

  const updateFieldValueByUniqueId = (
    fields: CreditApplicationFieldsValues[],
    uniqueId: string,
    value: unknown
  ): CreditApplicationFieldsValues[] =>
    fields.map((field) => {
      if (field.uniqueId === uniqueId) return { ...field, value };
      if (field.fieldValue?.some((fv) => fv.uniqueId === uniqueId)) {
        return {
          ...field,
          fieldValue: field.fieldValue.map((fv) =>
            fv.uniqueId === uniqueId ? { ...fv, value } : fv
          ),
        };
      }
      return field;
    });

  const handleEditFieldValue = (uniqueId: string, value: unknown) => {
    setEditableFieldValues((prev) => updateFieldValueByUniqueId(prev, uniqueId, value));
    setEditFieldErrors((prev) => {
      if (!prev[uniqueId]) return prev;
      const next = { ...prev };
      delete next[uniqueId];
      return next;
    });
  };

  const validateEditableFieldValues = (): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nextErrors: Record<string, string> = {};
    const originalFlat = flattenFieldValues(decryptedCreditApplication?.fieldValues ?? []);

    flattenFieldValues(editableFieldValues).forEach((field) => {
      const originalValue = originalFlat.find((f) => f.uniqueId === field.uniqueId)?.value;
      if (field.value === originalValue) return;

      let value = field.value;
      if (typeof value === 'string') value = value.trim();
      const isEmpty = value === '' || value === null || value === undefined;

      if (field.isRequired && isEmpty) {
        nextErrors[field.uniqueId] = `${field.name} is required.`;
        return;
      }
      if (isEmpty || field.fromField !== 'TEXT') return;

      if (field.isFieldEmail) {
        const emails = value
          .toString()
          .split(/[,;]/)
          .map((e: string) => e.trim())
          .filter(Boolean);
        const invalidEmail = emails.find((e: string) => !emailRegex.test(e));
        if (invalidEmail) {
          nextErrors[field.uniqueId] = `"${invalidEmail}" is not a valid email address.`;
        }
      } else if (field.validationType === 'EIN' && !/^\d{2}-\d{7}$/.test(value.toString().trim())) {
        nextErrors[
          field.uniqueId
        ] = `${field.name} must be in XX-XXXXXXX format (e.g. 12-3456789).`;
      } else if (
        field.validationType === 'SSN' &&
        !/^\d{3}-\d{2}-\d{4}$/.test(value.toString().trim())
      ) {
        nextErrors[
          field.uniqueId
        ] = `${field.name} must be in XXX-XX-XXXX format (e.g. 123-45-6789).`;
      } else if (
        field.validationType === 'PHONE' &&
        !/^\d{3}-\d{3}-\d{4}$/.test(value.toString().trim())
      ) {
        nextErrors[
          field.uniqueId
        ] = `${field.name} must be in XXX-XXX-XXXX format (e.g. 555-123-4567).`;
      }
    });

    setEditFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildFieldValuesForSave = (): CreditApplicationFieldsValues[] => {
    const secretKey = process.env.REACT_APP_CRYPTO_KEY;
    const originalDecryptedFlat = flattenFieldValues(decryptedCreditApplication?.fieldValues ?? []);
    const rawEncryptedFlat = flattenFieldValues(creditApplication?.fieldValues ?? []);

    const buildField = (field: CreditApplicationFieldsValues): CreditApplicationFieldsValues => {
      let nextField = field;

      if (field.isHashed) {
        const originalDecryptedValue = originalDecryptedFlat.find(
          (f) => f.uniqueId === field.uniqueId
        )?.value;
        const wasChanged = field.value !== originalDecryptedValue;

        if (!wasChanged) {
          const rawEncryptedField = rawEncryptedFlat.find((f) => f.uniqueId === field.uniqueId);
          nextField = rawEncryptedField ? { ...field, value: rawEncryptedField.value } : field;
        } else if (
          secretKey &&
          field.value !== null &&
          field.value !== undefined &&
          field.value !== ''
        ) {
          nextField = {
            ...field,
            value: CryptoJS.AES.encrypt(JSON.stringify(field.value), secretKey).toString(),
          };
        }
      }

      if (nextField.fieldValue) {
        nextField = {
          ...nextField,
          fieldValue: nextField.fieldValue.map(
            (fv) =>
              buildField(fv as unknown as CreditApplicationFieldsValues) as unknown as typeof fv
          ),
        };
      }

      return nextField;
    };

    return editableFieldValues.map(buildField);
  };

  const handleSaveApplicationData = async () => {
    if (!validateEditableFieldValues()) {
      openDillToast({
        message: 'Please fix the highlighted fields before saving.',
        type: 'ERROR',
      });
      return;
    }
    setSavingApplicationData(true);
    const response = await dispatch(
      editCreditApplicationData({
        creditApplicationId: currentCreditApplicationId,
        supplierId: currentSupplier?.id || '',
        fieldValues: buildFieldValuesForSave(),
      })
    );
    setSavingApplicationData(false);
    if (response && response.type.includes('fulfilled')) {
      openDillToast({
        message: 'Application data updated successfully!',
        type: 'SUCCESS',
      });
      setIsEditingApplicationData(false);
      setEditableFieldValues([]);
      setEditFieldErrors({});
    } else {
      openDillToast({
        message: 'Failed to update application data.',
        type: 'ERROR',
      });
    }
  };

  const handleLearnMore = (
    data: {
      id: string;
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      telephone: string;
      confidence: string | number | null;
      tradelines: string | number | null;
      status?: string;
      safeNo?: string;
      regNo?: string;
      id2?: string;
    }[],
    error: string,
    type: string,
    tab: 'personal' | 'business',
    page?: number
  ) => {
    setCreditReportCompanyList(data);
    setCreditReportError(error);
    setSelectedTab(tab);
    if (type === 'nacm') {
      setCreditReportCompanyListModalOpen(true);
    } else if (type === 'creditsafe') {
      setCreditSafeReportModalOpen(true);
    } else if (type === 'experian') {
      setExperianCompanyListModalOpen(true);
    }
    if (page) {
      setCreditSafePage(page);
    }
  };

  const handleViewCompanyReport = (data: string, nacmReportId: string) => {
    setNacmId(nacmReportId);
    setLearnMoreData(data);
    setLearnMoreModalOpen(true);
  };

  const handleViewCreditSafeReport = (data: any, creditSafeReportId: string) => {
    setCreditSafeId(creditSafeReportId);
    setCreditSafeData(data);
    setCreditSafeReportDetailsModalOpen(true);
  };

  const handleViewExperianReport = (data: any, bin: string) => {
    setExperianBin(bin);
    setExperianReportData(data);
    setExperianReportBufferData(null);
    setExperianReportModalOpen(true);
  };

  const handleChangeTradeRefDetails = (
    type: 'email' | 'company',
    field: CreditApplicationFieldsValues
  ) => {
    setTradeRefUpdateDetails({ type, field });
    setIsEditTradeReferenceModalOpen(true);
  };

  const handleSignatureEmailModal = (sigDetails: InvitedSignatures) => {
    setSignatureEmailModalOpen(true);
    setSignatureDetails(sigDetails);
  };

  const handleMakeSignatureRequired = async (sigDetails: InvitedSignatures) => {
    setSelectedSignature(sigDetails);
    setMakeSignatureModalOpen(true);
  };

  const handleResendSignature = async (sigDetails: InvitedSignatures) => {
    setCreditAppCommentModalType('resend-signature');
    setCreditAppCommentModalOpen(true);
    setSelectedSignature(sigDetails);
  };

  const handleRequestSignatureDetailsUpdate = async (sigDetails: InvitedSignatures) => {
    setCreditAppCommentModalType('request-signature-update');
    setCreditAppCommentModalOpen(true);
    setSelectedSignature(sigDetails);
  };

  const selectedBranch = useMemo(() => {
    if (decryptedCreditApplication?.branchId) {
      const currentBranch = supplierBranches.find(
        (branch) => branch.branchId === decryptedCreditApplication?.branchId
      );
      return currentBranch;
    }
    return {} as any;
  }, [decryptedCreditApplication, currentSupplier, supplierBranches]);

  // SupplierRefOption-shaped roster for resolving a supplier-sales-reps field's *label* (e.g.
  // "Sales Rep ID" vs "House #") based on whether the currently saved value's rep is a House Rep —
  // see resolveSalesRepFieldLabel's own doc comment. The saved *value* itself stays exactly as
  // configured (often the ERP ID) — only the label switches, never the value.
  const internalSignatureDocumentSalesReps = useMemo(() => {
    const seenSalesReps = new Set<string>();
    return supplierBranches
      .flatMap((branch) => branch?.salesRep ?? [])
      .filter((rep) => {
        const name = rep?.name ?? '';
        if (!name || seenSalesReps.has(name)) return false;
        seenSalesReps.add(name);
        return true;
      })
      .map((rep) => ({
        name: rep?.name ?? '',
        erpId: rep?.erpId ?? '',
        isHouseRep: rep?.isHouseRep ?? false,
      }));
  }, [currentSupplier, supplierBranches]);

  const selectedSalesRep = useMemo(() => {
    const assignedSalesRep = decryptedCreditApplication?.assignedSalesRep;
    if (!assignedSalesRep?.length) return [] as string[];

    const salesReps = supplierBranches.flatMap((branch) => branch?.salesRep ?? []);

    return Array.from(
      new Set(
        salesReps
          .filter((salesRep) => isBranchSalesRepAssigned(salesRep, assignedSalesRep))
          .map((salesRep) => salesRep.name)
      )
    );
  }, [decryptedCreditApplication?.assignedSalesRep, currentSupplier, supplierBranches]);

  const selectedRegion = useMemo(() => {
    if (!selectedBranch?.branchId) return undefined;
    return currentSupplier?.regions?.find((region) =>
      (region.branchIds ?? []).includes(selectedBranch.branchId)
    );
  }, [selectedBranch, currentSupplier]);

  const firstSelectedSalesRep = useMemo(() => {
    const assignedSalesRep = decryptedCreditApplication?.assignedSalesRep;
    if (!assignedSalesRep?.length) return undefined;
    const salesReps = supplierBranches.flatMap((branch) => branch?.salesRep ?? []);
    return salesReps.find((salesRep) => isBranchSalesRepAssigned(salesRep, assignedSalesRep));
  }, [decryptedCreditApplication?.assignedSalesRep, currentSupplier, supplierBranches]);

  const internalSignatureDocumentExtraValues = useMemo(
    () => ({
      branchName: selectedBranch?.branchName ?? '',
      branchErpId: selectedBranch?.erpId ?? '',
      salesRepName: firstSelectedSalesRep?.name ?? '',
      salesRepErpId: firstSelectedSalesRep?.erpId ?? '',
      territoryName: selectedRegion?.regionName ?? '',
    }),
    [selectedBranch, firstSelectedSalesRep, selectedRegion]
  );

  const status = decryptedCreditApplication?.status ?? '';
  const isCreditApp = !isCodApplicationType(decryptedCreditApplication?.applicationType);

  const isDecisionMaker = user?.roles.includes('CREDIT_APP_DECISION_MAKER');
  const isSalesRole = !user?.roles.includes('SUPPLIER');

  const isBusy = loadingStatus.includes('APPROVED') || loadingStatus.includes('REJECTED');

  const isFinalStatus = ['REJECTED', 'APPROVED', 'APPROVED_COD'].includes(status);

  const isLockedStatus = ['SENT', 'OPENED', 'DRAFT'].includes(status);

  const decisionDisabled = isBusy || isFinalStatus || isLockedStatus || !isDecisionMaker;

  const getGuarantorNames = (data: InvitedSignatures[]) => {
    return data
      .filter((item) => {
        if (item.type !== 'GUARANTOR' && item.type !== 'CORPORATE_GUARANTOR') return false;
        if (!item.signature) return false;

        return true;
      })
      .map((item) => {
        const fields = item.fields ?? [];

        const nameField = fields.find((f) => f.id.includes('name'));

        if (!nameField || !nameField.value) {
          return null;
        }

        return {
          name: nameField.value.trim(),
        };
      })
      .filter((item): item is { name: string } => item !== null);
  };

  const guarantors = useMemo(() => {
    if (decryptedCreditApplication?.invitedSignatures) {
      const invitedSignatures = decryptedCreditApplication?.invitedSignatures;
      const guarantorsName = getGuarantorNames(invitedSignatures);

      return guarantorsName;
    }
    return [] as { name: string }[];
  }, [decryptedCreditApplication]);

  const DecisionActions = () => {
    if (!decryptedCreditApplication) return null;

    const isEarlyStatus = [
      'SUBMITTED',
      'INTERNAL_SIGNATURE',
      'AWAITING_SIGNATURE',
      'DRAFT',
      'SENT',
      'OPENED',
    ].includes(status);
    const isFinalDecision = ['APPROVED', 'REJECTED'].includes(status);
    const isInternallyApproved = status.includes('INTERNALLY_APPROVED');

    return (
      <div className="w-full">
        {status === 'SUBMITTED' ||
        status === 'INTERNAL_SIGNATURE' ||
        status === 'AWAITING_SIGNATURE' ||
        status === 'DRAFT' ||
        status === 'SENT' ||
        status === 'OPENED' ? (
          <DillButton
            label="Ready for Decision"
            onClick={() => setReadyForDecisionModalOpen(true)}
            isDisabled={
              loadingStatus.includes('READY_FOR_DECISION') ||
              status === 'DRAFT' ||
              status === 'SENT' ||
              status === 'OPENED'
            }
            isLoading={loadingStatus.includes('READY_FOR_DECISION')}
            width="100%"
            height={40}
            className="w-full flex justify-center"
          />
        ) : (
          <div
            className={`relative w-full ${
              !isDecisionMaker ? 'border rounded-lg p-2 pt-10 bg-white' : ''
            }`}>
            {!isDecisionMaker && (
              <Lock
                data-tooltip-id={'user-not-decision-maker'}
                color={COLORS.ERROR_500}
                className="absolute top-1 right-1 ERROR_200-BG p-2 rounded-full w-8 h-8 z-40"
              />
            )}
            <div>
              {['APPROVED', 'REJECTED'].includes(status) ? (
                <div data-tooltip-id="user-not-decision-maker" className="w-full flex flex-col">
                  <DillButton
                    label="Revert Decision"
                    onClick={() => handleUpdateCreditApplication('SUBMITTED')}
                    isDisabled={loadingStatus.includes('SUBMITTED') || !isDecisionMaker}
                    isLoading={loadingStatus.includes('SUBMITTED')}
                    width="100%"
                    height={40}
                    className="w-full flex justify-center"
                  />
                </div>
              ) : (
                <div className="w-full">
                  {!status.includes('INTERNALLY_APPROVED') && status !== 'INTERNALLY_REJECTED' ? (
                    <div className="w-full grid grid-cols-2 gap-2">
                      {/* Reject Business */}
                      <div data-tooltip-id="user-not-decision-maker">
                        <DillButton
                          variant="secondary"
                          label="Reject Business"
                          onClick={() => {
                            setApproveEmailModalOpen(true);
                            setMode('INTERNALLY_REJECT');
                          }}
                          isDisabled={decisionDisabled}
                          isLoading={loadingStatus.includes('REJECTED_BUSINESS')}
                          width="100%"
                          height={40}
                          className="w-full flex justify-center"
                        />
                      </div>
                      <div data-tooltip-id="user-not-decision-maker" className="w-full">
                        {isCreditApp ? (
                          <DillButton
                            variant="secondary"
                            label="Approve COD"
                            onClick={() => {
                              setApproveEmailModalOpen(true);
                              setMode('INTERNALLY_APPROVE_COD');
                            }}
                            isDisabled={decisionDisabled}
                            width="100%"
                            height={40}
                            className="w-full flex justify-center"
                          />
                        ) : (
                          <DillButton
                            label="Approve Settings"
                            onClick={() => {
                              setApproveEmailModalOpen(true);
                              setMode('INTERNALLY_APPROVE');
                            }}
                            isDisabled={decisionDisabled}
                            variant="primary"
                            width="100%"
                            height={40}
                            className="w-full flex justify-center"
                          />
                        )}
                      </div>
                      {isCreditApp && (
                        <div data-tooltip-id="user-not-decision-maker" className="col-span-2">
                          <DillButton
                            label="Approve Credit Settings"
                            onClick={() => {
                              setApproveEmailModalOpen(true);
                              setMode('INTERNALLY_APPROVE');
                            }}
                            isDisabled={decisionDisabled}
                            variant="primary"
                            width="100%"
                            height={40}
                            className="w-full flex justify-center"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <div data-tooltip-id="user-not-decision-maker" className="w-full">
                        <DillButton
                          label="Revert Decision"
                          variant="secondary"
                          onClick={() => handleUpdateCreditApplication('SUBMITTED')}
                          isDisabled={loadingStatus.includes('SUBMITTED') || !isDecisionMaker}
                          isLoading={loadingStatus.includes('SUBMITTED')}
                          width="100%"
                          height={40}
                          className="w-full flex justify-center"
                        />
                      </div>
                      <div className="w-full">
                        <DillButton
                          label={'Notify Customer'}
                          onClick={() => {
                            setApproveEmailModalOpen(true);
                            setMode('NOTIFY');
                          }}
                          variant="primary"
                          width="100%"
                          height={40}
                          className="w-full flex justify-center"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const ApprovalSummaryCard = () => {
    const fieldValues = decryptedCreditApplication?.approvalAdditionalFieldValues ?? [];
    const amountAproved = decryptedCreditApplication?.amountAproved;
    const terms = decryptedCreditApplication?.terms;
    const customerAccountNumber = decryptedCreditApplication?.customerAccountNumber;
    const shipToAccountNumber = decryptedCreditApplication?.shipToAccountNumber;
    const template = decryptedCreditApplication?.templateId
      ? creditApplicationTemplates.find((t) => t.id === decryptedCreditApplication?.templateId)
      : creditApplicationTemplates.find(
          (t) =>
            t.supplierId === currentSupplier?.id &&
            t.applicationType === decryptedCreditApplication?.applicationType
        );
    const additionalFields = template?.approvalAdditionalFields ?? [];
    const hasAny =
      amountAproved ||
      terms ||
      customerAccountNumber ||
      shipToAccountNumber ||
      fieldValues.length > 0;
    if (!hasAny) return null;
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-2">
        {customerAccountNumber ? (
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Customer Account</span>
            <span className="text-xs font-semibold text-gray-800">{customerAccountNumber}</span>
          </div>
        ) : null}
        {shipToAccountNumber ? (
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Ship-To Account</span>
            <span className="text-xs font-semibold text-gray-800">{shipToAccountNumber}</span>
          </div>
        ) : null}
        {amountAproved ? (
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Amount Approved</span>
            <span className="text-xs font-semibold text-gray-800">
              {decryptedCreditApplication?.applicationType === 'CASH_APPLICATION'
                ? 'COD'
                : formatMoney(amountAproved, 0)}
            </span>
          </div>
        ) : null}
        {terms ? (
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Terms</span>
            <span className="text-xs font-semibold text-gray-800">{terms}</span>
          </div>
        ) : null}
        {fieldValues.map((entry) => {
          const fieldDef = additionalFields.find((f) => f.id === entry.id);
          const label = entry.name || fieldDef?.label || entry.id;
          const displayValue =
            fieldDef?.type === 'currency'
              ? formatMoney(parseFloat((entry.value ?? '').replace(/,/g, '')) || 0, 0)
              : entry.value ?? '';
          return (
            <div key={entry.id} className="flex justify-between items-center">
              <span className="text-xs text-gray-500 font-medium">{label}</span>
              <span className="text-xs font-semibold text-gray-800">{displayValue}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // General applicant/company info at a glance, distinct from ApprovalSummaryCard (which is
  // specifically the approval-side numbers: customer account, amount approved, terms).
  const ApplicationDetailsCard = () => {
    const manuallyAddedByLabel =
      decryptedCreditApplication?.manuallyAddedBy?.name ||
      decryptedCreditApplication?.manuallyAddedBy?.email;
    if (!manuallyAddedByLabel) return null;

    return (
      <div className="h-fit bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="px-4 py-3 flex flex-col gap-2">
          <div className="flex justify-between items-center gap-3">
            <span className="text-xs text-gray-500 font-medium">Manually Added By</span>
            <span className="text-xs font-semibold text-gray-800 text-right truncate">
              {manuallyAddedByLabel}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Emails only the pending candidates for one signer role — signing itself now only happens on
  // the standalone SignInternalSignatureDocument page (reached via that same emailed link), since a signer
  // here may only hold CREDIT_APP_SALES and has no access to this page's data.
  const handleSendInternalSignatureDocumentReminder = async (
    internalSignatureDocumentId: string,
    role: InternalSignatureDocumentSignerRole
  ) => {
    if (!currentSupplier?.id) return;
    setSendingReminderForRole(role);
    const result = await dispatch(
      sendInternalSignatureDocumentReminder({
        supplierId: currentSupplier.id,
        internalSignatureDocumentId,
        role,
      })
    );
    setSendingReminderForRole(null);
    if (result.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Reminder sent successfully.' });
      fetchInternalSignatureDocuments(internalSignatureDocumentsOwnerId);
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to send reminder.' });
    }
  };

  // Internal-only card(s) — never rendered on the applicant-facing flow (this whole page sits
  // under the authenticated supplier ProtectedRoute). Renders straight off each document's
  // `fieldValues`/`signers`, the same "field values populate the view" pattern used everywhere
  // else in this app — there's no separate generated PDF. A supplier may have more than one
  // differently-named internal document attached to the same application at once.
  const InternalSignatureDocumentsCard = () => {
    const relevantInternalSignatureDocuments = internalSignatureDocuments.filter(
      (cs) => cs.status !== 'NOT_REQUIRED'
    );
    if (relevantInternalSignatureDocuments.length === 0) return null;
    const statusLabel: Record<string, string> = {
      PENDING_SIGNATURES: 'Pending Signatures',
      PARTIALLY_SIGNED: 'Partially Signed',
      SIGNED: 'Signed',
    };
    // Shown when a signer's source resolved to zero candidates — worded per source since "assign
    // or update the branch" is meaningless for a reviewer/decision-maker-sourced signer.
    const noCandidatesMessage: Record<InternalSignatureDocumentSignerSource, string> = {
      BRANCH_TM:
        "No Territory Manager found for this application's branch — assign or update the branch to route this signature.",
      REGION_RSM:
        "No RSM found for this application's branch/region — assign or update the branch to route this signature.",
      CREDIT_APP_REVIEWER:
        'No reviewer is assigned to this application yet — assign a reviewer to route this signature.',
      CREDIT_APP_DECISION_MAKER:
        'No user has the Credit App Decision Maker role — grant that role to someone to route this signature.',
      SALES_REP:
        'No sales rep with a Dill account is assigned to this application — assign one to route this signature.',
    };
    return (
      // Own h2 heading (matching CreditReport's section-card convention) so it picks up as a
      // named step in the Steps nav (see the h2-scan effect above) — it renders right before
      // CreditReport, so it lands between whatever step precedes it and "Credit Score & Reports".
      <div className="flex w-full flex-col mt-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h2 className="text-sm font-bold text-gray-800 tracking-wide">
            Internal Signature Documents
          </h2>
        </div>
        <div className="flex flex-col gap-3 p-4">
          {relevantInternalSignatureDocuments.map((cs) => (
            <div
              key={cs.id}
              className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-800">
                  {cs.documentName || 'Internal Document'}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  {statusLabel[cs.status] ?? cs.status}
                </span>
              </div>
              {cs.formTemplate ? (
                <InternalSignatureDocumentTemplatePreview
                  formTemplate={cs.formTemplate}
                  fieldValues={cs.fieldValues}
                  fields={internalSignatureDocumentFieldsById[cs.id]}
                  signers={cs.signers}
                  extraValues={internalSignatureDocumentExtraValues}
                />
              ) : (
                <InternalSignatureDocumentPlainFieldList
                  fields={internalSignatureDocumentFieldsById[cs.id]}
                  fieldValues={cs.fieldValues}
                  salesReps={internalSignatureDocumentSalesReps}
                />
              )}
              {cs.signers
                .filter((s) => s.isRequired)
                .map((signer, signerIndex, requiredSigners) => {
                  // Signed in order (the definition's signInOrder): a later signer waits for
                  // everyone before them — no reminder until it's their turn.
                  const isSignedInOrder = !!creditApplicationTemplates
                    .find((t) => t.id === cs.creditApplicationTemplateId)
                    ?.internalSignatureDocuments?.find(
                      (definition) => definition.id === cs.internalSignatureDocumentDefinitionId
                    )?.signInOrder;
                  const waitingOn = isSignedInOrder
                    ? requiredSigners.slice(0, signerIndex).find((earlier) => !earlier.signature)
                    : undefined;
                  const pendingEmails = signer.candidates
                    .map((c) => c.email)
                    .filter((email): email is string => Boolean(email));
                  const signedCandidate = signer.candidates.find(
                    (c) => c.userId === signer.signedByUserId
                  );
                  const signerEmail = signedCandidate?.email ?? pendingEmails[0] ?? '';

                  if (signer.signature) {
                    return (
                      <div
                        key={signer.role}
                        className="grid grid-cols-2 gap-2 border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex flex-col items-start gap-1 p-3">
                          <span className="text-xs font-semibold text-gray-700">
                            {signer.roleLabel || signer.role}
                          </span>
                          <span className="text-xs text-gray-500">{signerEmail}</span>
                        </div>
                        <div className="flex flex-col items-start gap-1 p-3">
                          <div className="flex items-center gap-2">
                            <img
                              src={signer.signature}
                              alt="Signature"
                              className="h-6 w-auto object-contain"
                            />
                            <p className="text-[9px] text-gray-400">E-signed using Dill</p>
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {signer.dateSigned && `Date Signed: ${signer.dateSigned}`}
                            {signer.location && ` at ${signer.location}`}
                            {signer.ipAddress && ` from IP Address ${signer.ipAddress}`}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={signer.role}
                      className="flex justify-between items-center border-t border-gray-100 pt-2">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-700">
                          {signer.roleLabel || signer.role}
                        </span>
                        {waitingOn ? (
                          <span className="text-[10px] text-gray-500">
                            Waiting on {waitingOn.roleLabel || waitingOn.role} to sign first
                          </span>
                        ) : pendingEmails.length > 0 ? (
                          <span className="text-[10px] text-gray-500">
                            Pending — {pendingEmails.join(', ')}
                          </span>
                        ) : (
                          <span className="text-[10px] text-orange-500">
                            {noCandidatesMessage[signer.source]}
                          </span>
                        )}
                      </div>
                      {pendingEmails.length > 0 && !waitingOn ? (
                        <DillButton
                          label="Send Reminder"
                          variant="secondary"
                          icon={<PaperPlaneTilt size={14} />}
                          isLoading={sendingReminderForRole === signer.role}
                          onClick={() =>
                            handleSendInternalSignatureDocumentReminder(cs.id, signer.role)
                          }
                        />
                      ) : null}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const FEEDBACK_STATUS_STYLES = {
    SENT: { dot: 'bg-amber-400', text: 'text-amber-700' },
    IN_REVIEW: { dot: 'bg-blue-500', text: 'text-blue-700' },
    RESOLVED: { dot: 'bg-green-500', text: 'text-gray-500' },
  };

  const FeedbackStatusLine = ({
    item,
  }: {
    item: ApprovalStatusHistory & { feedbackStatus: 'SENT' | 'IN_REVIEW' | 'RESOLVED' };
  }) => {
    const label =
      item.feedbackStatus === 'RESOLVED'
        ? `Resolved${item.resolvedBy ? ` by ${item.resolvedBy}` : ''}${
            item.resolvedAt ? ` · ${moment(item.resolvedAt).format('MM/DD/YY')}` : ''
          }`
        : item.feedbackStatus === 'IN_REVIEW'
        ? `Customer responded${
            item.respondedAt ? ` ${moment(item.respondedAt).format('MM/DD/YY')}` : ''
          } · needs review`
        : 'Awaiting customer response';
    const styles = FEEDBACK_STATUS_STYLES[item.feedbackStatus];
    return (
      <div className={`flex items-center gap-1.5 mt-2 text-[11px] font-medium ${styles.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${styles.dot}`} />
        <span className="min-w-0">{label}</span>
      </div>
    );
  };

  const FeedbackSection = () => {
    if (!decryptedCreditApplication) return null;

    const feedbackDisabled = isFinalStatus || isLockedStatus;
    const history = decryptedCreditApplication?.approvalStatusHistory ?? [];
    const feedback = history
      .filter((stat) => stat.status === 'FEEDBACK_SENT' && stat.comment)
      .map((stat) => ({ ...stat, feedbackStatus: getFeedbackStatus(stat, history) }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const openFeedback = feedback.filter((item) => item.feedbackStatus !== 'RESOLVED');
    const inReviewCount = openFeedback.filter((item) => item.feedbackStatus === 'IN_REVIEW').length;

    const handleResolveFeedback = async (feedbackDate: string) => {
      if (!currentSupplier?.id) return;
      setResolvingFeedbackDate(feedbackDate);
      const result = await dispatch(
        resolveFeedbackCreditApplication({
          supplierId: currentSupplier.id,
          creditApplicationId: decryptedCreditApplication.id,
          applicationHistoryId: decryptedCreditApplication.applicationHistoryId,
          feedbackDate,
        })
      );
      setResolvingFeedbackDate(null);
      if (result.type.endsWith('/fulfilled')) {
        openDillToast({ type: 'SUCCESS', message: 'Feedback resolved.' });
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to resolve feedback.' });
      }
    };

    const initials = (name: string) =>
      name
        .split(' ')
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase() ?? '')
        .join('');

    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={(e) => {
              if (e.defaultPrevented) return;
              setIsFeedbackDropdownOpen(!isFeedbackDropdownOpen);
            }}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">Feedback</span>
              {openFeedback.length > 0 ? (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    inReviewCount > 0 ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                  }`}>
                  {inReviewCount > 0 ? `${inReviewCount} to review` : `${openFeedback.length} open`}
                </span>
              ) : (
                feedback.length > 0 && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                    {feedback.length}
                  </span>
                )
              )}
            </div>
            <div className="flex items-center gap-2">
              <DillButton
                variant="secondary"
                label="Send Feedback"
                onClick={(e) => {
                  e.preventDefault();
                  setFeedbackModal(true);
                }}
                isDisabled={feedbackDisabled}
              />
              <div
                className={`transition-transform duration-200 ${
                  isFeedbackDropdownOpen ? 'rotate-180' : 'rotate-0'
                }`}>
                <ChevronDown size={16} color={COLORS.GREY_500} />
              </div>
            </div>
          </div>

          {isFeedbackDropdownOpen && (
            <div className="mt-3">
              {feedback.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">No feedback sent yet.</p>
              ) : (
                <div className="flex flex-col gap-0 max-h-96 overflow-y-auto">
                  {feedback.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2.5 pb-4 last:pb-0 ${
                        item.feedbackStatus === 'RESOLVED' ? 'opacity-80' : ''
                      }`}>
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-[10px] font-bold shrink-0">
                          {initials(item.actionBy ?? '')}
                        </div>
                        {idx < feedback.length - 1 && (
                          <div className="w-px flex-1 bg-gray-100 mt-1" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="flex items-baseline justify-between gap-1 mb-1">
                          <span className="text-xs font-semibold text-gray-700 truncate">
                            {item.actionBy}
                          </span>
                          <span className="text-[10px] text-gray-400 shrink-0">
                            {moment(item.date).format('MM/DD/YY LT')}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.comment}</p>
                        {item?.fields && item.fields.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {item.fields.map((field, indx) => (
                              <span
                                key={indx + 'feedback-oo' + idx}
                                className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">
                                {field.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <FeedbackStatusLine item={item} />
                        {item.feedbackStatus !== 'RESOLVED' && (
                          <button
                            type="button"
                            onClick={() => handleResolveFeedback(new Date(item.date).toISOString())}
                            disabled={!!resolvingFeedbackDate}
                            className="flex items-center gap-1 mt-2 rounded-md border border-green-200 bg-white px-2 py-1 text-[11px] font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            <CheckCircle size={13} weight="bold" />
                            {resolvingFeedbackDate === new Date(item.date).toISOString()
                              ? 'Resolving...'
                              : 'Mark resolved'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <CreditAppNotes
            notes={decryptedCreditApplication?.internalNotes ?? []}
            creditAppId={decryptedCreditApplication.id}
            isSalesRole={isSalesRole}
            applicationHistoryId={decryptedCreditApplication.applicationHistoryId}
          />
        </div>
      </div>
    );
  };

  const AssignBranchBlock = () => (
    <div className="flex flex-col">
      <div className="h-fit bg-white border border-gray-200 rounded-xl shadow-sm ">
        <div className="flex items-center justify-between px-4 py-1 bg-gray-50 border-b border-gray-100 rounded-t-xl">
          <h3 className="text-sm font-bold text-gray-800 tracking-wide">Branch & Sales Reps</h3>
          <DillButton
            onClick={() => setAssignBranchModalOpen(true)}
            variant="ghost-blue"
            label={selectedBranch?.branchName ? 'Edit' : 'Assign'}
          />
        </div>
        <div className="px-4 py-3 flex flex-col gap-2">
          {selectedBranch?.branchName ? (
            <div>
              <p className="text-[11px] font-semibold text-gray-500 mb-0.5">Branch</p>
              <p className="text-sm text-gray-900 truncate">{formatBranchLabel(selectedBranch)}</p>
            </div>
          ) : (
            <button
              onClick={() => setAssignBranchModalOpen(true)}
              className="w-full text-xs text-gray-400 border border-dashed border-gray-300 rounded-lg py-3 hover:border-blue-300 hover:text-blue-500 transition-colors">
              Click to assign branch & sales reps
            </button>
          )}
          {selectedSalesRep.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-gray-500 mb-0.5">Sales Rep</p>
              <p className="text-sm text-gray-900 truncate">{selectedSalesRep.join(', ')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ReadyForDecisionModal
        open={readyForDecisionModalOpen}
        handleClose={() => setReadyForDecisionModalOpen(false)}
        creditApplication={decryptedCreditApplication}
        applicationId={currentCreditApplicationId}
      />
      <ShareApplicationModal
        open={shareApplicationModalOpen}
        handleClose={() => setShareApplicationModalOpen(false)}
        creditApplicationId={currentCreditApplicationId}
        applicationHistoryId={decryptedCreditApplication?.applicationHistoryId}
      />
      <SendInternalSignatureDocumentsModal
        open={sendInternalSignatureDocumentsModalOpen}
        handleClose={() => setSendInternalSignatureDocumentsModalOpen(false)}
        creditApplicationId={internalSignatureDocumentsOwnerId}
        onSuccess={() => fetchInternalSignatureDocuments(internalSignatureDocumentsOwnerId)}
      />
      {signatureDetails && (
        <EditSignatureEmailModal
          open={signatureEmailModalOpen}
          signatureDetails={signatureDetails}
          handleClose={() => setSignatureEmailModalOpen(false)}
          applicationId={currentCreditApplicationId}
        />
      )}
      {selectedSignature && currentSupplier && (
        <CreditAppCommentModal
          open={creditAppCommentModalOpen}
          sigDetails={selectedSignature}
          handleClose={() => setCreditAppCommentModalOpen(false)}
          creditApplicationId={currentCreditApplicationId}
          title={
            creditAppCommentModalType === 'request-signature-update'
              ? 'Request For Update Signature Details'
              : 'Resend Signature'
          }
          type={creditAppCommentModalType}
          onSuccess={
            creditAppCommentModalType === 'request-signature-update'
              ? () => {
                  dispatch(
                    getSupplierCreditApplication({
                      supplierId: currentSupplier.id || '',
                      creditApplicationId: currentCreditApplicationId,
                      hashedType: 'encrypted',
                      fetchType: 'detailed',
                    })
                  );
                }
              : undefined
          }
        />
      )}
      {selectedSignature && (
        <MakeSignatureRequiredModal
          open={makeSignatureModalOpen}
          invitedSignature={selectedSignature}
          handleClose={() => setMakeSignatureModalOpen(false)}
          applicationId={currentCreditApplicationId}
        />
      )}
      <RejectMessageModal
        open={isRejectModalOpen}
        handleClose={() => setIsRejectModalOpen(false)}
        onSubmit={(val) => {
          handleRejectApplication(
            'REJECTED_BUSINESS',
            decryptedCreditApplication?.applicationType ?? undefined,
            val
          );
        }}
      />
      <PlaidBalanceModal
        open={isPlaidBalanceModalOpen}
        handleClose={() => setIsPlaidBalanceModalOpen(false)}
        balanceDetails={bankDetails}
      />
      <CreditApplicationFeedbackModal
        open={feedbackModal}
        handleClose={() => setFeedbackModal(false)}
        creditApplication={decryptedCreditApplication}
        applicationHistoryId={decryptedCreditApplication?.applicationHistoryId}
      />
      <CreditApplicationNotesModal
        open={addNotesModal}
        handleClose={() => setAddNotesModal(false)}
        creditApplication={decryptedCreditApplication}
      />
      <AppSuccessModal
        open={isUpdateSubSupplierModalOpen}
        modalImage={successChecksCircle}
        title={`Successfully ${
          approvalStatus === 'REJECTED_WITH_COD'
            ? 'rejected but approved COD'
            : currentStatus === 'APPROVED'
            ? 'approved'
            : currentStatus === 'REJECTED'
            ? 'rejected'
            : 'updated status of'
        } ${getApplicationTypeName(decryptedCreditApplication?.applicationType)}!`}
        subTitle={`You have ${
          approvalStatus === 'REJECTED_WITH_COD'
            ? 'rejected but approved COD'
            : currentStatus === 'APPROVED'
            ? 'approved'
            : currentStatus === 'REJECTED'
            ? 'rejected'
            : 'changed the status of'
        } ${getApplicationTypeName(decryptedCreditApplication?.applicationType)}. ${
          currentStatus === 'APPROVED' || currentStatus === 'REJECTED'
            ? 'The company will be notified via email.'
            : ''
        }`}
        buttonText={`View ${applicationTypeTitle}`}
        handleClose={() => {
          setIsUpdateSubSupplierModalOpen(false);
        }}
      />
      <CreditAppAddTradeReferenceModal
        open={addTradeRefModalOpen}
        handleClose={() => setAddTradeeRefModalOpen(false)}
        creditApplication={creditApplication}
        tradeRefFields={selectedTradeRefFields}
      />
      <CreditSafeReportModal
        open={creditSafeReportDetailsModalOpen}
        handleClose={() => setCreditSafeReportDetailsModalOpen(false)}
        details={creditSafeData}
        overlayZIndex={52}
      />
      <CreditReportModal
        open={isCreditReportModalOpen}
        handleClose={() => setIsCreditReportModalOpen(false)}
        companyDetails={companyDetails}
        onLearnMore={handleLearnMore}
        creditApplicationId={currentCreditApplicationId}
        guarantors={guarantors}
        overlayZIndex={50}
      />
      {decryptedCreditApplication && mode && (
        <ApproveCreditAppEmailsModal
          open={approveEmailModalOpen}
          handleClose={() => setApproveEmailModalOpen(false)}
          creditApplication={decryptedCreditApplication}
          onSuccess={() => {
            setIsUpdateSubSupplierModalOpen(true);
          }}
          mode={mode}
          applicationHistoryId={decryptedCreditApplication?.applicationHistoryId}
          coversheetChargeAccountType={chargeAccountType}
          coversheetExistingAccountNumber={chargeExistingAccountNumber}
          internalSignatureDocuments={internalSignatureDocuments}
        />
      )}
      {decryptedCreditApplication && decryptedCreditApplication.approvalStatusHistory && (
        <CreditAppHistoryModal
          open={applicationHistoryModalOpen}
          handleClose={() => setApplicationHistoryModalOpen(false)}
          approvalStatusHistory={decryptedCreditApplication.approvalStatusHistory}
          onViewDetails={(approvalStatusHistory: ApprovalStatusHistory) => {
            setApprovalStatusHistoryDetails(approvalStatusHistory);
            setApprovalStatusHistoryDetailsModalOpen(true);
          }}
        />
      )}
      {approvalStatusHistoryDetails && (
        <CreditAppHistoryDetailsModal
          approvalStatusHistory={approvalStatusHistoryDetails}
          open={approvalStatusHistoryDetailsModalOpen}
          handleClose={() => setApprovalStatusHistoryDetailsModalOpen(false)}
        />
      )}
      <AppPdfPreviewModal
        open={previewModalOpen}
        handleClose={() => setPreviewModalOpen(false)}
        invoice={''}
        pdfType={'CREDIT_APPLICATION'}
        pdfList={[
          {
            url: currentPdfUrl,
            date: new Date(),
            name: '',
          },
        ]}
        handleDownloadPdf={() => {}}
        mode="Supplier"
      />
      {tradeRefUpdateDetails && (
        <UpdateTradeReferenceDetailsModal
          open={isEditTradeReferenceModalOpen}
          handleClose={() => setIsEditTradeReferenceModalOpen(false)}
          tradeReferenceDetails={tradeRefUpdateDetails}
          creditApplicationId={currentCreditApplicationId}
          applicationHistoryId={decryptedCreditApplication?.applicationHistoryId}
        />
      )}
      {!decryptedCreditApplication && !loadingList.includes('getSupplierCreditApplication') && (
        <NotFound
          buttonText="Go Back"
          title="Credit/COD Application Not Found"
          path="/creditApplications"
        />
      )}
      {currentCreditApplicationId && (
        <AssignBranchModal
          open={assignBranchModalOpen}
          handleClose={() => {
            setAssignBranchModalOpen(false);
          }}
          creditApplicationId={currentCreditApplicationId}
          applicationHistoryId={decryptedCreditApplication?.applicationHistoryId ?? null}
          currentApplication={decryptedCreditApplication}
          onSuccess={() => fetchInternalSignatureDocuments(internalSignatureDocumentsOwnerId)}
        />
      )}
      <CreditReportCompanyListModal
        open={creditReportCompanyListModalOpen}
        handleClose={() => setCreditReportCompanyListModalOpen(false)}
        data={creditReportCompanyList}
        onLearnMore={handleViewCompanyReport}
        error={creditReportError}
        creditApplicationId={decryptedCreditApplication?.id ?? ''}
        companyDetails={companyDetails}
      />
      <CreditSafeReportCompanyModal
        open={creditSafeReportModalOpen}
        handleClose={() => setCreditSafeReportModalOpen(false)}
        data={creditReportCompanyList}
        onLearnMore={handleViewCreditSafeReport}
        error={creditReportError}
        creditApplicationId={decryptedCreditApplication?.id ?? ''}
        companyDetails={companyDetails}
        defaultTotal={creditSafePage}
        selectedTab={selectedTab}
        overlayZIndex={51}
      />
      <NACMCreditReportModal
        open={learnMoreModalOpen}
        handleClose={() => setLearnMoreModalOpen(false)}
        details={learnMoreData}
        nacmId={nacmId}
        creditApplicationId={currentCreditApplicationId}
      />
      <ExperianReportCompanyListModal
        open={experianCompanyListModalOpen}
        handleClose={() => setExperianCompanyListModalOpen(false)}
        data={creditReportCompanyList as any}
        onLearnMore={handleViewExperianReport}
        error={creditReportError}
        creditApplicationId={decryptedCreditApplication?.id ?? ''}
        companyDetails={companyDetails}
      />
      <ExperianCreditReportModal
        open={experianReportModalOpen}
        handleClose={() => setExperianReportModalOpen(false)}
        details={experianReportData}
        bufferData={experianReportBufferData}
        experianBin={experianBin}
        creditApplicationId={currentCreditApplicationId}
      />
      {loadingList.includes('getSupplierCreditApplication') && (
        <div className="w-full h-full flex p-4 gap-4 animate-pulse">
          <div className="w-3/4 bg-white rounded-lg p-4 flex flex-col gap-4">
            <div className="flex justify-between items-center pb-2 border-b">
              <div className="flex items-center gap-2">
                <div className="h-5 w-40 bg-gray-200 rounded-md" />
                <div className="h-5 w-20 bg-gray-100 rounded-full" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-8 w-24 bg-gray-200 rounded-lg" />
                <div className="h-8 w-28 bg-gray-200 rounded-lg" />
                <div className="h-8 w-24 bg-gray-200 rounded-lg" />
                <div className="h-8 w-20 bg-gray-200 rounded-lg" />
                <div className="h-8 w-8 bg-gray-200 rounded-lg" />
              </div>
            </div>
            <div className="flex flex-col gap-6 mt-2">
              <div className="flex flex-col gap-3">
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <div className="h-3 w-24 bg-gray-100 rounded" />
                      <div className="h-9 w-full bg-gray-200 rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <div className="h-3 w-20 bg-gray-100 rounded" />
                      <div className="h-9 w-full bg-gray-200 rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="h-4 w-36 bg-gray-200 rounded" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <div className="h-3 w-28 bg-gray-100 rounded" />
                      <div className="h-9 w-full bg-gray-200 rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="w-1/4 flex flex-col gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
              <div className="h-4 w-28 bg-gray-200 rounded" />
              <div className="h-9 w-full bg-gray-100 rounded-lg" />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <div className="h-4 w-16 bg-gray-200 rounded" />
              </div>
              <div className="py-1 flex flex-col gap-1 px-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2.5 py-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-200 shrink-0" />
                    <div className="h-3 bg-gray-200 rounded" style={{ width: `${55 + i * 8}%` }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-9 w-full bg-gray-200 rounded-lg" />
              <div className="h-9 w-full bg-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      )}
      {decryptedCreditApplication && (
        <div>
          {modalState === ModalState.DILL_APPLICATION && (
            <div className="w-full h-full flex  p-4 gap-4">
              <div className="w-3/4 min-w-0 WHITE-BG p-4 rounded-lg">
                <div className="flex justify-between items-center w-full mb-2 pb-2 border-b">
                  {/* Title + duplicate badge */}
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className="text-base font-bold text-gray-900 capitalize truncate">
                      {`${applicationTypeTitle} Document`}
                    </h1>
                    {decryptedCreditApplication?.isDuplicate &&
                      creditApplication?.status !== 'DRAFT' && (
                        <>
                          <div
                            data-tooltip-id={`is-duplicate-${decryptedCreditApplication.id}`}
                            className="flex shrink-0 items-center gap-1 px-2 py-0.5 bg-yellow-50 border border-yellow-200 rounded-full text-[11px] font-semibold text-yellow-700 cursor-default">
                            <Flag size={11} weight="fill" />
                            <span>Duplicate EIN</span>
                          </div>
                          <ReactTooltip
                            id={`is-duplicate-${decryptedCreditApplication.id}`}
                            place="top"
                            variant="dark"
                            opacity={'100%'}
                            style={{
                              background: '#334155',
                              width: '250px',
                              zIndex: '70',
                              borderRadius: '8px',
                              fontWeight: '600',
                            }}>
                            <span className="text-xs">
                              A credit application with this EIN already exists
                            </span>
                          </ReactTooltip>
                        </>
                      )}
                  </div>

                  {/* Status + actions */}
                  <div className="flex shrink-0 gap-2 items-center ml-4">
                    <div className="flex flex-col items-end">
                      <AppCreditApplicationStatus
                        status={decryptedCreditApplication.status}
                        wrapperClassName="w-fit text-nowrap"
                      />
                      {decryptedCreditApplication.status === 'DRAFT' &&
                        decryptedCreditApplication?.currentStep && (
                          <div className="text-[10px] capitalize PRIMARY_500-CLR mt-0.5">
                            {`Step: ${decryptedCreditApplication?.currentStep.stepNumber}/${
                              decryptedCreditApplication?.currentStep?.totalSteps ?? ''
                            }`}
                          </div>
                        )}
                    </div>
                    {creditApplication?.applicationUpdateHistory &&
                      creditApplication.applicationUpdateHistory.length > 0 && (
                        <DillDropdownButton
                          label={
                            decryptedCreditApplication?.applicationHistoryId
                              ? `Version ${
                                  [...(creditApplication.applicationUpdateHistory ?? [])]
                                    .sort(
                                      (a, b) =>
                                        new Date(a.createdAt ?? 0).getTime() -
                                        new Date(b.createdAt ?? 0).getTime()
                                    )
                                    .findIndex(
                                      (h) =>
                                        h.id === decryptedCreditApplication.applicationHistoryId
                                    ) + 2
                                }`
                              : 'Version 1 (Original)'
                          }
                          showChevron
                          variant="secondary"
                          height={35}
                          options={[
                            { label: 'Version 1 (Original)', value: '' },
                            ...[...(creditApplication.applicationUpdateHistory ?? [])]
                              .sort(
                                (a, b) =>
                                  new Date(a.createdAt ?? 0).getTime() -
                                  new Date(b.createdAt ?? 0).getTime()
                              )
                              .map((h, idx) => ({
                                label: `Version ${idx + 2}`,
                                value: h.id,
                              })),
                          ]}
                          onSelect={(opt) =>
                            navigate(
                              `/creditApplications/${creditApplication.id}${
                                opt.value ? `?historyId=${opt.value}` : ''
                              }`
                            )
                          }
                        />
                      )}
                    {decryptedCreditApplication &&
                      decryptedCreditApplication.approvalStatusHistory && (
                        <DillButton
                          label="View History"
                          variant="secondary"
                          height={35}
                          className="flex justify-center rounded-md"
                          icon={<ClockCounterClockwise size={16} weight="bold" />}
                          onClick={() => {
                            setApplicationHistoryModalOpen(true);
                          }}
                        />
                      )}
                    {!isEditingApplicationData ? (
                      <DillDropdownButton
                        label="Actions"
                        variant="secondary"
                        height={35}
                        showChevron
                        isLoading={loadingType === 'download' || loadingType === 'preview'}
                        options={[
                          {
                            label: 'Download',
                            value: 'download',
                            icon: (
                              <DownloadSimple color={COLORS.GREY_500} size={14} weight="bold" />
                            ),
                            isDisabled: loadingType !== '',
                          },
                          {
                            label: 'Preview',
                            value: 'preview',
                            icon: <Eye color={COLORS.GREY_500} size={14} />,
                            isDisabled: loadingType !== '',
                          },
                          {
                            label: 'Share Application',
                            value: 'share',
                            icon: <ShareNetwork size={14} />,
                          },
                          {
                            label: 'Edit Application',
                            value: 'edit',
                            icon: <PencilSimple size={14} weight="bold" />,
                          },
                        ]}
                        onSelect={(opt) => {
                          if (opt.value === 'download') {
                            handlePreviewOrDownload('download');
                          } else if (opt.value === 'preview') {
                            handlePreviewOrDownload('preview');
                          } else if (opt.value === 'share') {
                            setShareApplicationModalOpen(true);
                          } else if (opt.value === 'edit') {
                            handleStartEditingApplicationData();
                          }
                        }}
                      />
                    ) : (
                      <>
                        <DillButton
                          label="Cancel"
                          variant="secondary"
                          height={35}
                          className="flex justify-center rounded-md"
                          isDisabled={savingApplicationData}
                          onClick={handleCancelEditingApplicationData}
                        />
                        <DillButton
                          label="Save Changes"
                          variant="primary"
                          height={35}
                          className="flex justify-center rounded-md"
                          isLoading={savingApplicationData}
                          isDisabled={savingApplicationData}
                          onClick={handleSaveApplicationData}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div
                  ref={scrollContainerRef}
                  className="h-[calc(100vh-180px)] overflow-y-scroll w-full">
                  <CreditApplicationSummaryStep
                    creditApplication={decryptedCreditApplication}
                    formData={previewFormData}
                    pulledCreditReports={pulledCreditReports}
                    onViewFullReport={() => handleStepClick('Credit Score & Reports')}
                    onSelectCheckFields={handleSelectFraudCheckFields}
                  />
                  <PreviewCreditApplicationData
                    formData={previewFormData}
                    onFollowUp={(fields: CreditApplicationFieldsValues[], indx: number) =>
                      handleEmailAction(fields, indx, 'followUp')
                    }
                    onSendEmail={(fields: CreditApplicationFieldsValues[], indx: number) =>
                      handleEmailAction(fields, indx, 'initial')
                    }
                    onAddTradeReference={(fields: CreditApplicationFieldsValues[]) =>
                      handleAddTradeReference(fields)
                    }
                    loadingButton={sending}
                    bankReference={decryptedCreditApplication?.bankReference}
                    onGetPlaidDetails={handleGetPlaidDetails}
                    onRefreshPlaidDetails={handleRefreshPlaidDetails}
                    plaidLoadingStatus={viewBankReferenceStatus}
                    inviteSignatures={decryptedCreditApplication.invitedSignatures}
                    currentCreditApplicationId={currentCreditApplicationId}
                    isPreview={true}
                    onUploadTradeReferenceResponse={handleUploadTradeReferenceResponse}
                    loadingTradeReferenceResponse={uploadTradeReferenceResponse}
                    tradeReferenceResponse={decryptedCreditApplication.tradeReferenceResponse}
                    onDeleteTradeReferenceFile={handleDeleteTradeReferenceFile}
                    onChangeTradeRefDetails={handleChangeTradeRefDetails}
                    tradeRefenceHistory={decryptedCreditApplication.tradeReferenceHistory}
                    onEditSignatureEmail={handleSignatureEmailModal}
                    onResendSignature={handleResendSignature}
                    onRequestSignatureDetailsUpdate={handleRequestSignatureDetailsUpdate}
                    onMakeSignatureRequired={handleMakeSignatureRequired}
                    applicationHistoryId={decryptedCreditApplication.applicationHistoryId}
                    isEditingData={isEditingApplicationData}
                    editableFieldValues={editableFieldValues}
                    onEditFieldValue={handleEditFieldValue}
                    editFieldErrors={editFieldErrors}
                  />
                  {InternalSignatureDocumentsCard()}
                  {currentSupplier && (
                    <CreditReport
                      pulledCreditReports={pulledCreditReports}
                      onSeeCreditReport={() => setIsCreditReportModalOpen(true)}
                      onPreviewReport={(data) => {
                        if (data.source === 'NACM') {
                          setNacmId(data.id);
                          setLearnMoreData(data.html);
                          setLearnMoreModalOpen(true);
                        } else if (data.source === 'Experian') {
                          setExperianBin(data.id);
                          setExperianReportData(data.html);
                          setExperianReportBufferData(data.bufferData ?? null);
                          setExperianReportModalOpen(true);
                        } else {
                          setCreditSafeId(data.id);
                          setCreditSafeData(data.html);
                          setCreditSafeReportDetailsModalOpen(true);
                        }
                      }}
                      creditApplicationId={currentCreditApplicationId}
                      supplier={currentSupplier}
                      creditApplication={decryptedCreditApplication}
                    />
                  )}
                </div>
              </div>
              <div className="w-1/4 flex flex-col gap-4 h-[calc(100vh-90px)] overflow-y-scroll">
                <AssignBranchBlock />
                <div className="flex flex-col">
                  <div className="h-fit bg-white border border-gray-200 rounded-xl shadow-sm ">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100 rounded-t-xl">
                      <h3 className="text-sm font-bold text-gray-800 tracking-wide">Steps</h3>
                    </div>
                    <div className="py-1">
                      {allSteps.map((step, index) => {
                        const isActive = activeStep === step;
                        return (
                          <div
                            key={index}
                            onClick={() => handleStepClick(step)}
                            className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-all duration-200 cursor-pointer ${
                              isActive
                                ? 'bg-blue-50 text-blue-500 font-semibold'
                                : 'text-gray-500 hover:bg-gray-50'
                            }`}>
                            <div
                              className={`w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-200 ${
                                isActive ? 'bg-blue-500' : 'bg-gray-300'
                              }`}
                            />
                            <span className="truncate">{step}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {ApplicationDetailsCard()}
                {ApprovalSummaryCard()}
                <div className="rounded-xl border border-gray-100 bg-white p-4">
                  {DecisionActions()}
                </div>
                {FeedbackSection()}
              </div>
            </div>
          )}
          {modalState === ModalState.MANUAL_UPLOAD_APPLICATION && (
            <div className="w-full">
              {decryptedCreditApplication?.applicationPdf && (
                <div className="w-full flex p-4 gap-4">
                  <div className="flex flex-col w-3/4 WHITE-BG p-4 rounded-2xl items-center">
                    <div className="flex justify-between w-full mb-2 pb-2 border-b">
                      <div>
                        <div className="w-full font-bold text-lg capitalize">
                          {`${applicationTypeTitle} Document`}
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <div className="flex flex-col">
                          <AppCreditApplicationStatus
                            status={decryptedCreditApplication.status}
                            wrapperClassName="w-fit text-nowrap"
                          />
                        </div>
                        {decryptedCreditApplication &&
                          decryptedCreditApplication.approvalStatusHistory && (
                            <DillButton
                              label="View History"
                              variant="secondary"
                              height={35}
                              className="rounded-md"
                              icon={<ClockCounterClockwise color={COLORS.GREY_500} size={16} />}
                              onClick={() => {
                                setApplicationHistoryModalOpen(true);
                              }}
                            />
                          )}
                        {decryptedCreditApplication?.applicationPdf?.url && (
                          <DillButton
                            label="Download"
                            variant="secondary"
                            height={35}
                            className="rounded-md"
                            icon={
                              <DownloadSimple color={COLORS.GREY_500} size={16} weight="bold" />
                            }
                            onClick={() => {
                              const applicationPdf = decryptedCreditApplication.applicationPdf;
                              if (!applicationPdf?.url) return;
                              downloadFile(
                                applicationPdf.url,
                                applicationPdf.name || 'credit-application.pdf'
                              );
                            }}
                          />
                        )}
                        <div data-tooltip-id="share-credit-application">
                          <DillButton
                            variant="secondary"
                            width={35}
                            height={35}
                            className="rounded-md"
                            icon={<ShareNetwork size={16} />}
                            onClick={() => {
                              setShareApplicationModalOpen(true);
                            }}
                          />
                        </div>
                        <div data-tooltip-id="send-charge-sheet">
                          <DillButton
                            variant="secondary"
                            width={35}
                            height={35}
                            className="rounded-md"
                            icon={<PaperPlaneTilt size={16} />}
                            onClick={() => {
                              setSendInternalSignatureDocumentsModalOpen(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col h-[calc(100vh-210px)] overflow-scroll items-center w-full">
                      <div>
                        <div
                          style={{
                            width: '612px',
                            background: '#F4F7FA',
                          }}
                          className="sticky top-0  z-10 flex justify-between  mb-0.5"></div>

                        <div
                          style={{
                            width: '612px',
                            height: `calc(100vh - 250px)`,
                          }}
                          className="relative flex justify-center">
                          {decryptedCreditApplication?.applicationPdf?.url && (
                            <Viewer
                              defaultScale={1}
                              fileUrl={decryptedCreditApplication?.applicationPdf?.url}
                              plugins={[zoomPluginInstance, pageNavigationPluginInstance]}
                            />
                          )}
                          {!decryptedCreditApplication?.applicationPdf?.url && (
                            <p className="text-sm self-center ">No file selected</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-row justify-center w-full">
                        <div className="mx-4">
                          <CurrentPageLabel>
                            {(props: RenderCurrentPageLabelProps) => (
                              <span>{`page ${props.currentPage + 1} / ${
                                props.numberOfPages
                              }`}</span>
                            )}
                          </CurrentPageLabel>
                        </div>

                        <ZoomOutButton />
                        <ZoomPopover />
                        <ZoomInButton />
                      </div>
                      <div className="w-full">
                        <div className="text-sm font-semibold mb-2 border-b pb-1 mt-2 w-full"></div>
                        <div className="w-full">
                          <PreviewCreditApplicationData
                            formData={previewFormData}
                            onFollowUp={(fields: CreditApplicationFieldsValues[], indx: number) =>
                              handleEmailAction(fields, indx, 'followUp')
                            }
                            onSendEmail={(fields: CreditApplicationFieldsValues[], indx: number) =>
                              handleEmailAction(fields, indx, 'initial')
                            }
                            loadingButton={sending}
                            bankReference={decryptedCreditApplication?.bankReference}
                            onGetPlaidDetails={handleGetPlaidDetails}
                            plaidLoadingStatus={viewBankReferenceStatus}
                            inviteSignatures={decryptedCreditApplication.invitedSignatures}
                            currentCreditApplicationId={currentCreditApplicationId}
                            isPreview={true}
                            onUploadTradeReferenceResponse={handleUploadTradeReferenceResponse}
                            loadingTradeReferenceResponse={uploadTradeReferenceResponse}
                            tradeReferenceResponse={
                              decryptedCreditApplication.tradeReferenceResponse
                            }
                            onDeleteTradeReferenceFile={handleDeleteTradeReferenceFile}
                            onChangeTradeRefDetails={handleChangeTradeRefDetails}
                            tradeRefenceHistory={decryptedCreditApplication.tradeReferenceHistory}
                            onEditSignatureEmail={handleSignatureEmailModal}
                            onResendSignature={handleResendSignature}
                            onRequestSignatureDetailsUpdate={handleRequestSignatureDetailsUpdate}
                            applicationHistoryId={decryptedCreditApplication.applicationHistoryId}
                          />
                          {InternalSignatureDocumentsCard()}
                          {user && currentSupplier && (
                            <CreditReport
                              pulledCreditReports={pulledCreditReports}
                              onSeeCreditReport={() => setIsCreditReportModalOpen(true)}
                              onPreviewReport={(data) => {
                                if (data.source === 'NACM') {
                                  setNacmId(data.id);
                                  setLearnMoreData(data.html);
                                  setLearnMoreModalOpen(true);
                                } else if (data.source === 'Experian') {
                                  setExperianBin(data.id);
                                  setExperianReportData(data.html);
                                  setExperianReportBufferData(data.bufferData ?? null);
                                  setExperianReportModalOpen(true);
                                } else {
                                  setCreditSafeId(data.id);
                                  setCreditSafeData(data.html);
                                  setCreditSafeReportDetailsModalOpen(true);
                                }
                              }}
                              creditApplicationId={currentCreditApplicationId}
                              supplier={currentSupplier}
                              creditApplication={decryptedCreditApplication}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-1/4 flex flex-col gap-4 h-[calc(100vh-120px)] overflow-y-scroll">
                    <AssignBranchBlock />
                    {ApplicationDetailsCard()}
                    {ApprovalSummaryCard()}
                    <div className="rounded-xl border border-gray-100 bg-white p-4">
                      {DecisionActions()}
                    </div>
                    {FeedbackSection()}
                  </div>
                </div>
              )}
            </div>
          )}
          {modalState === ModalState.NOT_READY && (
            <div className="w-full h-[calc(100vh-120px)] flex flex-col items-center justify-center gap-6 text-center px-8">
              <div
                className="flex items-center justify-center rounded-full p-5"
                style={{ backgroundColor: '#EFF6FF' }}>
                <Lock size={40} color={COLORS.PRIMARY_500} weight="light" />
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-lg" style={{ color: '#1E293B' }}>
                  {decryptedCreditApplication?.status === 'OPENED'
                    ? 'Application In Progress'
                    : 'Awaiting Response'}
                </p>
                <p className="text-sm max-w-sm" style={{ color: '#64748B' }}>
                  {decryptedCreditApplication?.status === 'OPENED'
                    ? 'The applicant has opened this application but has not yet submitted it. Check back once they complete it.'
                    : 'This application has been sent to the applicant. Details will appear here once they complete and submit it.'}
                </p>
              </div>
              {decryptedCreditApplication?.approvalStatusHistory && (
                <DillButton
                  label="View History"
                  variant="secondary"
                  height={35}
                  className="flex justify-center rounded-md"
                  icon={<ClockCounterClockwise size={16} weight="bold" />}
                  onClick={() => setApplicationHistoryModalOpen(true)}
                />
              )}
              <div className="w-full max-w-md rounded-xl border border-gray-100 bg-white p-4 text-left">
                <CreditAppNotes
                  notes={decryptedCreditApplication?.internalNotes ?? []}
                  creditAppId={decryptedCreditApplication.id}
                  isSalesRole={isSalesRole}
                  applicationHistoryId={decryptedCreditApplication.applicationHistoryId}
                  defaultOpen
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!user?.roles.includes('CREDIT_APP_DECISION_MAKER') && (
        <ReactTooltip
          id={'user-not-decision-maker'}
          place="right"
          variant="dark"
          opacity={'100%'}
          style={{
            background: '#222',
            width: '300px',
            zIndex: 99999,
          }}>
          <span className="text-xs">
            You currently don’t have permissions to approve credit applications. Please revisit your
            settings or reach out to customer support.
          </span>
        </ReactTooltip>
      )}
      <ReactTooltip
        id={'share-credit-application'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '150px',
          zIndex: 99999,
        }}>
        <span className="text-xs">Share Application</span>
      </ReactTooltip>
    </div>
  );
};

export default CreditApplicationDetails;
